// @ts-nocheck
import { ECONOMY_EVENT, economyAccount, economyRequest, startEconomyBattle, pendingBattleId } from './lib/economyClient';
import { applyEconomySnapshot, preserveOnlineAssets } from './lib/economyState';
import { calculateSummonCost } from './engine/summonCost';
import { SaveRecoveryScreen } from './collection/components/SaveRecoveryScreen';
import { STORAGE } from './collection/data/constants';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  INITIAL_LIFE,
  INITIAL_SP,
  MAX_SP,
  INITIAL_HAND_SIZE,
  COUNTER_ATTACK_RATE,
  PHASES,
  PIERCING_DAMAGE_RATE,
  LONE_WARRIOR_BONUS,
  RULE_LONE_WARRIOR,
  TYPE_ICONS,
  ATTRIBUTE_COLORS,
} from './utils/constants';
import { shuffle, createDeck, createMonsterInstance, createDeckFromPrebuilt, createDeckFromUserDeck } from './utils/helpers';
import { getDeckOptions } from './decks/prebuiltDecks';
import { loadCardsFromCSV, SAMPLE_CARDS } from './utils/cardManager';
import { executeSkillEffects } from './engine/effectEngine';
import {
  registerCardTriggers,
  unregisterCardTriggers,
  fireTrigger,
  fireLeaveFieldTrigger,
  clearAllTriggers,
  resetTurnFlags,
  getCardMainPhaseTriggers,
  getCardGraveyardTriggers,
  hasCardTrigger,
  parseCardTriggers,
  activateTrigger,
} from './engine/triggerEngine';
import { TRIGGER_TYPES, ACTIVATION_TYPES, getTriggerDisplayName } from './engine/triggerTypes';
import {
  getPhaseCardStageText,
  getStageName,
  getStageShortName,
  getCurrentStageDescription,
  getNextStageDescription,
} from './engine/phaseCardEffects';
import { continuousEffectEngine } from './engine/continuousEffects';
import { statusEffectEngine, STATUS_EFFECT_TYPES, getStatusDisplayName, getStatusIcon, STATUS_EFFECT_METADATA } from './engine/statusEffects';
import { processStatusEffectsTurnStart, processStatusEffectsEndPhase } from './engine/effectHelpers';
import {
  isSetsunaMagic,
  CHAIN_POINTS,
  CHAIN_POINT_NAMES,
  processLinkEndPhaseDamage,
  handleLinkBreak,
  shouldApplyShishoku,
  shouldApplyDokushin,
  canActivateSourei,
  getSoureiEffect,
  findSameNameCardInHand,
  canActivateGigen,
  calculateGigenReduction,
  canIgnoreBlockWithIatsuko,
  canActivateMiraiYochi,
  canActivateHengenmi,
  getDirectAttackStatus,
} from './engine/keywordAbilities';
import {
  // AI制御関連はuseAIControllerに移動（Phase 7）
  AI_SPEED_SETTINGS,
  getAIThinkingSpeed,
  setAIThinkingSpeed,
  getScaledDelay,
  getPlayerDeckSettings,
  savePlayerDeckSettings,
} from './engine/ai';

// GameEngine (ヘッドレス対戦用)
import {
  useGameEngine,
  actions as gameActions, // Phase B: dispatch用アクションクリエイター
} from './engine/gameEngine';
import styles from './styles/gameStyles';
import mobileStyles, { mergeStyles } from './styles/mobileStyles';
import { useIsMobile } from './collection/hooks/useMediaQuery';
import Card from './components/Card';
import FieldMonster from './components/FieldMonster';
import SPTokens from './components/SPTokens';
import GameLog from './components/GameLog';
import DeckReviewModal from './components/DeckReviewModal';

// 画面コンポーネント（Phase 1: 分離）
import { TitleScreen, GameOverScreen, MenuScreen } from './screens';

// バトルコンポーネント（Phase 2: 分離）
import { ChainConfirmationModal, GraveyardViewerModal, MobileCardDetailModal, PlayerArea, CenterZone, TopBar, ActionFooter, SetsunaModal, SelectionOverlay, DeepSeaModal } from './battle/components';

// バトルHooks（Phase 3, 4, 6, 7: 分離）
import { useBattleSelection, useSetsunaMagic, useGigen, useMiraiYochi, useSourei, useHengenmi, useDeepSea, usePhaseProcessor, useSummon, useCombat, useSkillExecution, useAIController } from './battle/hooks';
// バトルContext（Phase 5）
import { BattleProvider } from './battle/context';

// ヘルプシステム
import { HelpModal, HelpButton } from './help';

// 認証システム
import { useAuth } from './lib/useAuth';
import { saveBattleRewardToServer, processInvestmentDayOnServer } from './lib/supabase';

// コレクションシステム
import {
  storage,
  createInitialPlayerData,
  validatePlayerData,
  repairPlayerData,
  currencyManager,
  valueCalculator,
  CollectionScreen,
  PackOpening,
  DeckBuilder,
  DeckList,
  MarketAnalysis,
  MerchantGuild,
  MerchantShop,
  CardDexScreen,
  advanceDay,
  recordPriceHistory,
  calculateMarketModifier,
  getCardMarketPrice,
  recordAssetSnapshot,
  getMarketIndexChartData,
  getTrendIcon,
  getTrendColor,
  generateSparklineData,
  // 大会システム
  validateBet,
  addBet,
  removeBet,
  checkTournamentTrigger,
  createTournament,
  runTournament,
  updateTournamentStatus,
  calculatePayouts,
  createHistoryEntry,
  updateTotalStats,
  TOURNAMENT_STATUS,
  TournamentViewer,
} from './collection';

import {
  purchaseInfo,
  validateInfoPurchase,
  getInfoPrice,
} from './collection/tournament';

// トーナメント賭けサーバー連携
import {
  confirmBetsOnServer,
  claimPayoutsOnServer,
  isBettingServerAvailable,
  convertLocalBetsToServerFormat,
} from './collection/tournament/systems/bettingSystemServer';

// 商人システム
import {
  MERCHANTS,
  generateStock,
  getTodayAppearances,
  countForbiddenCards,
} from './collection/merchant';

// 投資システム
import {
  recordDailyFundPrices,
  applyDailySwap,
  applyMaintenanceCost,
  processAutoOrders,
  processLeverageLiquidations,
  processLiquidations,
  processDividends,
  createDebt,
  addToDebt,
  hasDebt,
  applyDailyInterest,
  isDebtOverdue,
  getDebtSummary,
  SeizureScreen,
  AutoBattleLoop,
  DebtStatus,
} from './collection/investment';
import { recordDailyFXRates } from './collection/investment/systems/fxManager';
import { FX_PAIRS } from './collection/investment/data/fxPairs';
import { buildMarketIndex } from './collection/market';
import { useGlobalMarket } from './lib/useGlobalMarket';
import { TwinKeyApp } from './twinKey';
import { DraftApp, loadDraftSession, saveDraftSession } from './draft';
import AIAnalysisScreen from './collection/components/AIAnalysisScreen';

// アドベンチャーモード
import { AdventureScreen } from './adventure';
import type { AdventureBattleConfig, BattleRewardResult, BattleResult } from './adventure';
// アドベンチャー関連のロジックは useAdventure.handleBattleEnd に集約

// ========================================
// 効果テキストから基本技・上級技を除外するヘルパー関数
// （情報パネルでの重複表示を防ぐため）
// ========================================
const getEffectWithoutSkills = (effectText) => {
  if (!effectText) return effectText;
  // 基本技と上級技の記述を削除（句点または文末まで）
  return effectText
    .replace(/基本技[：:][^。\n]+[。]?/g, '')
    .replace(/上級技[：:][^。\n]+[。]?/g, '')
    .trim();
};

// メインゲームコンポーネント
// ========================================
export default function MagicSpiritGame() {
  // ========================================
  // GameEngine (ヘッドレス対戦シミュレーション用)
  // 段階的移行: 現在は初期化のみ、将来的に状態管理を統合
  // ========================================
  const {
    state: engineState,
    dispatch,
    initGame: engineInitGame,
    resetGame: engineResetGame,
  } = useGameEngine();

  // ========================================
  // Phase D-4完了: ゲーム状態はengineStateが唯一の状態源泉
  // セッターはdispatch経由でengineStateを更新
  // ========================================

  // ゲーム進行状態（6個）- engineState直接参照
  const turn = engineState?.turn ?? 1;
  const currentPlayer = engineState?.currentPlayer ?? 1;
  const phase = engineState?.phase ?? 0;
  const isFirstTurn = engineState?.isFirstTurn ?? true;
  const winner = engineState?.winner ?? null;
  const logs = engineState?.logs ?? [];

  // カードデータ管理
  const [allCards, setAllCards] = useState(SAMPLE_CARDS);
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [persistenceIssue, setPersistenceIssue] = useState(null);

  // ゲーム状態
  const [gameState, setGameState] = useState('title'); // title, duelSetup, playing, gameOver, collection, packOpening, deckList, deckEdit, merchantGuild, merchantShop, seizure, autoBattleLoop
  const [merchantGuildInitialTab, setMerchantGuildInitialTab] = useState('merchant'); // 商人ギルド初期タブ
  const [packTabInitialState, setPackTabInitialState] = useState(null); // パックタブの初期選択状態 { packType, attribute }
  const [autoBattleInitialDebt, setAutoBattleInitialDebt] = useState(0); // 自動対戦ループ開始時の借金額
  // Phase D-3: turn, currentPlayer, phase, isFirstTurn, winner, logs のuseStateは削除
  // これらは engineState から直接参照（上部の互換レイヤー変数を使用）

  // モバイル対応
  const isMobile = useIsMobile();
  const [showSidePanel, setShowSidePanel] = useState(false); // モバイル時のサイドパネルモーダル
  const [cardInfoPosition, setCardInfoPosition] = useState<'left' | 'right' | 'hidden'>('left'); // カード情報パネル位置

  // ヘルプモーダル
  const [showHelp, setShowHelp] = useState(false);

  // コレクションシステム状態
  const [playerData, setPlayerData] = useState(null); // プレイヤーデータ（コレクション、G等）
  const [cardValueMap, setCardValueMap] = useState(null); // カード価値マップ
  const [pendingPackCards, setPendingPackCards] = useState(null); // 開封待ちパックカード
  const [battleReward, setBattleReward] = useState(null); // 対戦報酬 { goldReward, packReward, isWin }
  const [editingDeck, setEditingDeck] = useState(null); // デッキ編集中のデッキ（nullなら新規）
  const [showMarketAnalysis, setShowMarketAnalysis] = useState(false); // 市場分析画面表示
  const [currentMerchant, setCurrentMerchant] = useState(null); // 現在訪問中の商人名
  const [showTournamentViewer, setShowTournamentViewer] = useState(false); // 大会観戦ダイアログ表示
  const [pendingTournamentResult, setPendingTournamentResult] = useState(null); // 報酬受け取り待ち大会結果
  const isClaimingTournamentReward = useRef(false); // 大会報酬請求中フラグ（重複防止）
  const [showRestDialog, setShowRestDialog] = useState(false); // 休息完了ダイアログ表示（MenuScreenで使用）
  const [investmentResultData, setInvestmentResultData] = useState(null); // 投資処理結果（MenuScreenで使用）
  const [draftBattleContext, setDraftBattleContext] = useState(null); // ドラフトバトルコンテキスト { playerDeck, opponentDeck, opponentName, onBattleEnd }
  const [adventureBattleContext, setAdventureBattleContext] = useState(null); // アドベンチャーバトルコンテキスト { npcId, npcName, onBattleEnd }

  // ========================================
  // グローバル市場データ（サーバーから取得）
  // ========================================
  const {
    prices: serverPrices,
    indexes: serverIndexes,
    news: serverNews,
    chainNews: serverChainNews,
    newsHistory: serverNewsHistory,
    activeTrend: serverActiveTrend,
    spotlightEvent: serverSpotlightEvent,
    suddenEvent: serverSuddenEvent,
    overnightVolatility: serverOvernightVolatility,
    persistentModifiers: serverPersistentModifiers,
    loading: marketLoading,
    timestamp: marketTimestamp,
  } = useGlobalMarket();

  // ========================================
  // 認証状態
  // ========================================
  const authState = useAuth();

  // ========================================
  // Phase D-4: プレイヤー状態（engineStateから直接参照）
  // ========================================

  // プレイヤー1の状態
  const p1Life = engineState?.p1?.life ?? INITIAL_LIFE;
  const p1Deck = engineState?.p1?.deck ?? [];
  const p1Hand = engineState?.p1?.hand ?? [];
  const p1Field = engineState?.p1?.field ?? [null, null, null, null, null];
  const p1Graveyard = engineState?.p1?.graveyard ?? [];
  const p1ActiveSP = engineState?.p1?.activeSP ?? INITIAL_SP;
  const p1RestedSP = engineState?.p1?.restedSP ?? 0;
  const p1FieldCard = engineState?.p1?.fieldCard ?? null;
  const p1PhaseCard = engineState?.p1?.phaseCard ?? null;
  const p1StatusEffects = engineState?.p1?.statusEffects ?? [];
  const p1NextTurnSPBonus = engineState?.p1?.nextTurnSPBonus ?? 0;
  const p1MagicBlocked = engineState?.p1?.magicBlocked ?? false;
  const p1SpReduction = engineState?.p1?.spReduction ?? 0;
  const p1BonusSP = engineState?.p1?.bonusSP ?? 0;

  // プレイヤー2の状態
  const p2Life = engineState?.p2?.life ?? INITIAL_LIFE;
  const p2Deck = engineState?.p2?.deck ?? [];
  const p2Hand = engineState?.p2?.hand ?? [];
  const p2Field = engineState?.p2?.field ?? [null, null, null, null, null];
  const p2Graveyard = engineState?.p2?.graveyard ?? [];
  const p2ActiveSP = engineState?.p2?.activeSP ?? INITIAL_SP;
  const p2RestedSP = engineState?.p2?.restedSP ?? 0;
  const p2FieldCard = engineState?.p2?.fieldCard ?? null;
  const p2PhaseCard = engineState?.p2?.phaseCard ?? null;
  const p2StatusEffects = engineState?.p2?.statusEffects ?? [];
  const p2NextTurnSPBonus = engineState?.p2?.nextTurnSPBonus ?? 0;
  const p2MagicBlocked = engineState?.p2?.magicBlocked ?? false;
  const p2SpReduction = engineState?.p2?.spReduction ?? 0;
  const p2BonusSP = engineState?.p2?.bonusSP ?? 0;

  // 先攻プレイヤー（後攻ボーナス判定用）
  const firstPlayer = engineState?.firstPlayer ?? 1;

  // Phase D-4: ターンフラグ（engineStateから直接参照）
  const chargeUsedThisTurn = engineState?.turnFlags?.chargeUsedThisTurn ?? false;
  const pendingOpponentSPRest = engineState?.turnFlags?.pendingOpponentSPRest ?? null;

  // UI状態（Phase 3: useBattleSelection Hookに集約）
  // Phase 5: Context用にオブジェクトとして保持
  const selection = useBattleSelection();
  const {
    // State
    selectedHandCard,
    selectedFieldMonster,
    attackingMonster,
    selectedFieldCardInfo,
    hoveredCard,
    mobileCardDetail,
    showGraveyardViewer,
    pendingHandSelection,
    pendingSelectedCard,
    pendingGraveyardSelection,
    pendingGraveyardSelectedCard,
    pendingDeckReview,
    pendingMonsterTarget,
    pendingSelectedMonsterIndex,
    pendingCageRelease,
    // Setters
    setSelectedHandCard,
    setSelectedFieldMonster,
    setAttackingMonster,
    setSelectedFieldCardInfo,
    setHoveredCard,
    setMobileCardDetail,
    setShowGraveyardViewer,
    setPendingHandSelection,
    setPendingSelectedCard,
    setPendingGraveyardSelection,
    setPendingGraveyardSelectedCard,
    setPendingDeckReview,
    setPendingMonsterTarget,
    setPendingSelectedMonsterIndex,
    setPendingCageRelease,
    // Utilities
    resetSelection,
    resetAllPending,
    resetAll: resetAllSelection,
  } = selection;
  // pendingCageRelease = {
  //   monsterName: string,
  //   slotIndex: number,
  //   fieldPlayer: 1 | 2,
  //   spCost: number,
  // }

  // 刹那詠唱状態 → Phase 4-1: useSetsunaMagic Hook に移動（addLog定義後に呼び出し）

  // キーワード能力状態 → Phase 4-2: useGigen, useMiraiYochi, useSourei Hooks に移動

  // デッキ選択状態・AIプレイヤー設定（localStorageから復元）
  const [p1SelectedDeck, setP1SelectedDeckState] = useState(() => getPlayerDeckSettings().p1SelectedDeck);
  const [p2SelectedDeck, setP2SelectedDeckState] = useState(() => getPlayerDeckSettings().p2SelectedDeck);
  const [p1PlayerType, setP1PlayerTypeState] = useState(() => getPlayerDeckSettings().p1PlayerType);
  const [p2PlayerType, setP2PlayerTypeState] = useState(() => getPlayerDeckSettings().p2PlayerType);
  const [p1AIDifficulty, setP1AIDifficultyState] = useState(() => getPlayerDeckSettings().p1AIDifficulty);
  const [p2AIDifficulty, setP2AIDifficultyState] = useState(() => getPlayerDeckSettings().p2AIDifficulty);
  const [aiThinkingSpeed, setAiThinkingSpeedState] = useState(() => getAIThinkingSpeed()); // 'normal' | 'fast' | 'veryFast'
  const [summoningSickness, setSummoningSicknessState] = useState(() => getPlayerDeckSettings().summoningSickness); // 召喚酔い（デフォルト: OFF）
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false); // 詳細設定パネル表示
  const [secondPlayerBonusSP, setSecondPlayerBonusSPState] = useState(() => getPlayerDeckSettings().secondPlayerBonusSP ?? false); // 後攻ボーナスSP（デフォルト: OFF）
  const [piercingDamage, setPiercingDamageState] = useState(() => getPlayerDeckSettings().piercingDamage ?? false); // 貫通ダメージ（デフォルト: OFF）
  const [loneWarriorRule, setLoneWarriorRuleState] = useState(() => getPlayerDeckSettings().loneWarriorRule ?? true); // 孤軍奮闘（デフォルト: ON）
  const [initialLifePoints, setInitialLifePointsState] = useState(() => getPlayerDeckSettings().initialLifePoints ?? 6000); // 初期LP（デフォルト: 6000）
  // AI状態は useAIController Hook に移動（Phase 7）

  // プレイヤー設定変更ハンドラー（localStorageに保存）
  const setP1SelectedDeck = useCallback((value) => {
    setP1SelectedDeckState(value);
    savePlayerDeckSettings({ p1SelectedDeck: value });
  }, []);
  const setP2SelectedDeck = useCallback((value) => {
    setP2SelectedDeckState(value);
    savePlayerDeckSettings({ p2SelectedDeck: value });
  }, []);
  const setP1PlayerType = useCallback((value) => {
    setP1PlayerTypeState(value);
    savePlayerDeckSettings({ p1PlayerType: value });
  }, []);
  const setP2PlayerType = useCallback((value) => {
    setP2PlayerTypeState(value);
    savePlayerDeckSettings({ p2PlayerType: value });
  }, []);
  const setP1AIDifficulty = useCallback((value) => {
    setP1AIDifficultyState(value);
    savePlayerDeckSettings({ p1AIDifficulty: value });
  }, []);
  const setP2AIDifficulty = useCallback((value) => {
    setP2AIDifficultyState(value);
    savePlayerDeckSettings({ p2AIDifficulty: value });
  }, []);
  const setSummoningSickness = useCallback((value) => {
    setSummoningSicknessState(value);
    savePlayerDeckSettings({ summoningSickness: value });
  }, []);
  const setSecondPlayerBonusSP = useCallback((value) => {
    setSecondPlayerBonusSPState(value);
    savePlayerDeckSettings({ secondPlayerBonusSP: value });
  }, []);
  const setPiercingDamage = useCallback((value) => {
    setPiercingDamageState(value);
    savePlayerDeckSettings({ piercingDamage: value });
  }, []);
  const setLoneWarriorRule = useCallback((value) => {
    setLoneWarriorRuleState(value);
    savePlayerDeckSettings({ loneWarriorRule: value });
  }, []);
  const setInitialLifePoints = useCallback((value) => {
    const numValue = Math.max(1000, Math.min(99999, parseInt(value) || 6000));
    setInitialLifePointsState(numValue);
    savePlayerDeckSettings({ initialLifePoints: numValue });
  }, []);

  // ========================================
  // Android 戻るボタン対応（History API）
  // ========================================

  // 画面遷移の親子関係定義
  const SCREEN_HIERARCHY = {
    'title': null,              // ルート画面（戻るとアプリ終了）
    'menu': null,               // メニュー画面も戻らない（タイトルに戻す必要なし）
    'collection': 'menu',       // コレクション → メニュー
    'deckList': 'menu',         // デッキリスト → メニュー
    'deckEdit': 'deckList',     // デッキ編集 → デッキリスト
    'cardDex': 'menu',          // 図鑑 → メニュー
    'merchantGuild': 'menu',    // 商人ギルド → メニュー
    'merchantShop': 'merchantGuild', // 商人ショップ → 商人ギルド
    'twinKey': 'menu',          // ツインキー → メニュー（内部でも独自の戻る処理あり）
    'draft': 'menu',            // ドラフト → メニュー
    'aiAnalysis': 'menu',       // AI分析 → メニュー
    'duelSetup': 'menu',        // デュエル設定 → メニュー
    'playing': null,            // プレイ中は戻れない（ゲーム中断防止）
    'gameOver': 'menu',         // ゲームオーバー → メニュー
    'autoBattleLoop': null,     // 自動対戦中は戻れない
    'packOpening': null,        // パック開封中は戻れない
    'seizure': null,            // 差し押さえ中は戻れない
  };

  // popstateからの遷移かどうかを追跡（二重履歴追加防止）
  const isPopstateTransitionRef = useRef(false);
  const prevGameStateRef = useRef(gameState);

  // gameState変更時に自動でhistory追加
  useEffect(() => {
    // popstateからの遷移の場合は履歴を追加しない
    if (isPopstateTransitionRef.current) {
      isPopstateTransitionRef.current = false;
      prevGameStateRef.current = gameState;
      return;
    }

    // 画面が変わった場合にのみ履歴を追加
    if (prevGameStateRef.current !== gameState) {
      window.history.pushState({ screen: gameState, timestamp: Date.now() }, '', '');
      prevGameStateRef.current = gameState;
    }
  }, [gameState]);

  // popstateイベントハンドラ（戻るボタン押下時）
  useEffect(() => {
    const handlePopState = (event) => {
      // TwinKey画面の場合は、TwinKey内部でpopstateを処理する
      // ただし、履歴状態がメイン画面を示している場合は処理する
      if (gameState === 'twinKey') {
        const historyScreen = event.state?.screen;
        // 履歴がtwinKey以外を示している場合（TwinKey→メニューへの戻り）
        if (historyScreen && historyScreen !== 'twinKey') {
          isPopstateTransitionRef.current = true;
          setGameState(historyScreen);
        }
        // それ以外はTwinKey内部のpopstateハンドラに任せる
        return;
      }

      // 図鑑画面の場合は、図鑑内部でpopstateを処理する
      // cardDexView情報がある場合は図鑑内部の履歴がある
      if (gameState === 'cardDex') {
        const historyState = event.state;
        // 図鑑内部の遷移の場合は図鑑のハンドラに任せる
        if (historyState?.cardDexView) {
          return;
        }
        // cardDex以外への遷移（図鑑→メニュー）の場合はここで処理
        if (historyState?.screen && historyState.screen !== 'cardDex') {
          isPopstateTransitionRef.current = true;
          setGameState(historyState.screen);
          return;
        }
        // 履歴がない場合はメニューに戻る
        isPopstateTransitionRef.current = true;
        setGameState('menu');
        return;
      }

      // 戻れない画面の場合は履歴を元に戻して無視
      const currentScreen = gameState;
      const parentScreen = SCREEN_HIERARCHY[currentScreen];

      if (parentScreen === null) {
        // 戻れない画面（playing, autoBattleLoop, packOpening, seizure, title）
        // 履歴を元に戻す（戻るボタンを無効化）
        window.history.pushState({ screen: currentScreen, timestamp: Date.now() }, '', '');
        return;
      }

      // 親画面に戻る
      if (parentScreen) {
        // popstateからの遷移であることをマーク（履歴二重追加防止）
        isPopstateTransitionRef.current = true;

        // 商人ショップから戻る場合は商人をクリア
        if (currentScreen === 'merchantShop') {
          setCurrentMerchant(null);
        }
        // デッキ編集から戻る場合は編集中デッキをクリア
        if (currentScreen === 'deckEdit') {
          setEditingDeck(null);
        }

        setGameState(parentScreen);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // 初期状態をhistoryに設定
    if (!window.history.state?.screen) {
      window.history.replaceState({ screen: gameState, timestamp: Date.now() }, '', '');
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [gameState]);

  // AI思考速度変更ハンドラー（localStorageに保存）
  const handleAiThinkingSpeedChange = useCallback((speed) => {
    setAiThinkingSpeedState(speed);
    setAIThinkingSpeed(speed);
  }, []);

  // ログ追加関数（Phase D-3: dispatch経由）
  const addLog = useCallback((message, type = 'info') => {
    dispatch(gameActions.addLog(message, type));
  }, [dispatch]);

  // ========================================
  // Phase D-4-2: コンテキストアダプター
  // effectHelpers/cardEffects/cardTriggersからのset*呼び出しをdispatch経由に変換
  // ========================================

  // Phase D-4: useState削除後のセッター（dispatch経由）
  // 関数形式はreducer（applyUpdatePlayerState）で解決される

  // ライフ
  const setP1Life = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { life: value }));
  }, [dispatch]);
  const setP2Life = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { life: value }));
  }, [dispatch]);

  // デッキ
  const setP1Deck = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { deck: value }));
  }, [dispatch]);
  const setP2Deck = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { deck: value }));
  }, [dispatch]);

  // 手札
  const setP1Hand = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { hand: value }));
  }, [dispatch]);
  const setP2Hand = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { hand: value }));
  }, [dispatch]);

  // フィールド
  const setP1Field = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { field: value }));
  }, [dispatch]);
  const setP2Field = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { field: value }));
  }, [dispatch]);

  // 墓地
  const setP1Graveyard = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { graveyard: value }));
  }, [dispatch]);
  const setP2Graveyard = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { graveyard: value }));
  }, [dispatch]);

  // SP
  const setP1ActiveSP = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { activeSP: value }));
  }, [dispatch]);
  const setP2ActiveSP = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { activeSP: value }));
  }, [dispatch]);
  const setP1RestedSP = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { restedSP: value }));
  }, [dispatch]);
  const setP2RestedSP = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { restedSP: value }));
  }, [dispatch]);

  // フィールドカード・フェイズカード
  const setP1FieldCard = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { fieldCard: value }));
  }, [dispatch]);
  const setP2FieldCard = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { fieldCard: value }));
  }, [dispatch]);
  const setP1PhaseCard = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { phaseCard: value }));
  }, [dispatch]);
  const setP2PhaseCard = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { phaseCard: value }));
  }, [dispatch]);

  // 状態異常
  const setP1StatusEffects = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { statusEffects: value }));
  }, [dispatch]);
  const setP2StatusEffects = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { statusEffects: value }));
  }, [dispatch]);

  // SPボーナス
  const setP1NextTurnSPBonus = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { nextTurnSPBonus: value }));
  }, [dispatch]);
  const setP2NextTurnSPBonus = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { nextTurnSPBonus: value }));
  }, [dispatch]);

  // 魔法ブロック
  const setP1MagicBlocked = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { magicBlocked: value }));
  }, [dispatch]);
  const setP2MagicBlocked = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { magicBlocked: value }));
  }, [dispatch]);

  // SP減少
  const setP1SpReduction = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { spReduction: value }));
  }, [dispatch]);
  const setP2SpReduction = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { spReduction: value }));
  }, [dispatch]);

  // ボーナスSP（後攻ボーナス）
  const setP1BonusSP = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(1, { bonusSP: value }));
  }, [dispatch]);
  const setP2BonusSP = useCallback((value) => {
    dispatch(gameActions.updatePlayerState(2, { bonusSP: value }));
  }, [dispatch]);

  // ターンフラグ
  const setChargeUsedThisTurn = useCallback((value) => {
    dispatch(gameActions.setGameFlags({ chargeUsedThisTurn: value }));
  }, [dispatch]);

  // ゲームフラグ設定（効果コンテキスト用）
  const setGameFlags = useCallback((flags) => {
    dispatch(gameActions.setGameFlags(flags));
  }, [dispatch]);

  // ========================================
  // Phase 4-1: 刹那詠唱Hook
  // Phase 5: Context用にオブジェクトとして保持
  // ========================================
  const setsuna = useSetsunaMagic({
    currentPlayer,
    p1Hand, p2Hand,
    p1ActiveSP, p2ActiveSP,
    p1Field, p2Field,
    p1Life, p2Life,
    p1Deck, p2Deck,
    p1Graveyard, p2Graveyard,
    p1MagicBlocked, p2MagicBlocked,
    addLog,
    setP1ActiveSP, setP2ActiveSP,
    setP1RestedSP, setP2RestedSP,
    setP1Hand, setP2Hand,
    setP1Graveyard, setP2Graveyard,
    setP1Life, setP2Life,
    setP1Field, setP2Field,
    setP1Deck, setP2Deck,
    setPendingMonsterTarget,
    setPendingHandSelection,
    setPendingDeckReview,
  });
  const {
    setsunaPendingActivation,
    setsunaPendingCard,
    chainConfirmation,
    pendingSetsunaAction,
    setSetsunaPendingActivation,
    setSetsunaPendingCard,
    setChainConfirmation,
    setPendingSetsunaAction,
    startChainConfirmation,
    skipChainConfirmation,
    activateSetsunaInChain,
    cancelChainAndAction,
    canUseSetsunaMagic,
    getSetsunaMagicsForNonActivePlayer,
    activateSetsunaMagic,
    getSetsunaActualCost,
    filterActivatableSetsunaCards,
    startSetsunaMagicMode,
    cancelSetsunaMagicMode,
    selectSetsunaMagicCard,
    resetAll: resetAllSetsuna,
  } = setsuna;

  // skipChainConfirmation wrapper: hook内で直接 setPendingSetsunaAction を呼び出すようになった
  // AI制御との互換性のためラッパーを維持
  const handleSkipChainConfirmation = useCallback(() => {
    skipChainConfirmation();
  }, [skipChainConfirmation]);

  // ========================================
  // Phase 4-2: キーワード能力Hooks
  // Phase 5: Context用にオブジェクトとして保持
  // ========================================

  // 【壮麗】Hook
  const sourei = useSourei({
    currentPlayer,
    p1Hand, p2Hand,
    p1Field, p2Field,
    addLog,
    setP1Hand, setP2Hand,
    setP1Field, setP2Field,
    setP1Graveyard, setP2Graveyard,
    setP1SpReduction, setP2SpReduction,
    setPendingMonsterTarget,
  });
  const { activateSourei } = sourei;

  // 【未来予知】Hook
  const miraiYochi = useMiraiYochi({
    currentPlayer,
    phase,
    p1Field, p2Field,
    p1Deck, p2Deck,
    addLog,
    setP1Field, setP2Field,
    setP1Deck, setP2Deck,
    setPendingDeckReview,
  });
  const {
    pendingMiraiYochi,
    setPendingMiraiYochi,
    activateMiraiYochi,
    completeMiraiYochi,
    cancelMiraiYochi,
  } = miraiYochi;

  // 【犠現】Hook
  const gigen = useGigen({
    currentPlayer,
    p1ActiveSP, p2ActiveSP,
    p1Field, p2Field,
    p1Hand, p2Hand,
    p1Deck, p2Deck,
    p1Graveyard, p2Graveyard,
    p1Life, p2Life,
    summoningSickness,
    addLog,
    setP1Field, setP2Field,
    setP1Hand, setP2Hand,
    setP1Deck, setP2Deck,
    setP1Graveyard, setP2Graveyard,
    setP1ActiveSP, setP2ActiveSP,
    setP1RestedSP, setP2RestedSP,
    setP1Life, setP2Life,
    setPendingMonsterTarget,
    setPendingHandSelection,
    setPendingGraveyardSelection,
    setShowGraveyardViewer,
    setPendingDeckReview,
    setSelectedHandCard,
  });
  const {
    pendingGigenActivation,
    setPendingGigenActivation,
    startGigenActivation,
    completeGigenSummon,
    cancelGigenActivation,
  } = gigen;

  // 【変幻身】Hook
  const hengenmi = useHengenmi({
    currentPlayer,
    phase,
    p1ActiveSP, p2ActiveSP,
    p1Field, p2Field,
    p1Deck, p2Deck,
    addLog,
    setP1Field, setP2Field,
    setP1Deck, setP2Deck,
    setP1ActiveSP, setP2ActiveSP,
    setP1RestedSP, setP2RestedSP,
    setSelectedFieldMonster,
  });
  const { activateHengenmi } = hengenmi;

  // 深海領域Hook
  const deepSea = useDeepSea();

  // ========================================
  // Phase 6-1: フェイズ処理Hook
  // ========================================
  const { processPhase } = usePhaseProcessor({
    // ゲーム状態（読み取り）
    currentPlayer,
    isFirstTurn,
    firstPlayer,
    turn,
    pendingOpponentSPRest,
    summoningSickness,
    secondPlayerBonusSP,

    // プレイヤー1 状態
    p1Life,
    p1Field,
    p1Hand,
    p1Deck,
    p1Graveyard,
    p1ActiveSP,
    p1RestedSP,
    p1FieldCard,
    p1PhaseCard,
    p1StatusEffects,
    p1NextTurnSPBonus,
    p1SpReduction,
    p1BonusSP,
    p1MagicBlocked,

    // プレイヤー2 状態
    p2Life,
    p2Field,
    p2Hand,
    p2Deck,
    p2Graveyard,
    p2ActiveSP,
    p2RestedSP,
    p2FieldCard,
    p2PhaseCard,
    p2StatusEffects,
    p2NextTurnSPBonus,
    p2SpReduction,
    p2BonusSP,
    p2MagicBlocked,

    // 深海領域
    p1DeepSeaZone: deepSea.p1DeepSeaZone,
    p2DeepSeaZone: deepSea.p2DeepSeaZone,

    // Setters - プレイヤー1
    setP1Life,
    setP1Field,
    setP1Hand,
    setP1Deck,
    setP1Graveyard,
    setP1ActiveSP,
    setP1RestedSP,
    setP1FieldCard,
    setP1PhaseCard,
    setP1StatusEffects,
    setP1NextTurnSPBonus,
    setP1SpReduction,
    setP1BonusSP,
    setP1MagicBlocked,

    // Setters - プレイヤー2
    setP2Life,
    setP2Field,
    setP2Hand,
    setP2Deck,
    setP2Graveyard,
    setP2ActiveSP,
    setP2RestedSP,
    setP2FieldCard,
    setP2PhaseCard,
    setP2StatusEffects,
    setP2NextTurnSPBonus,
    setP2SpReduction,
    setP2BonusSP,
    setP2MagicBlocked,

    // Setters - 深海領域
    setP1DeepSeaZone: deepSea.setP1DeepSeaZone,
    setP2DeepSeaZone: deepSea.setP2DeepSeaZone,

    // UI Setters
    setChargeUsedThisTurn,
    setPendingCageRelease,
    setPendingHandSelection,
    setPendingGraveyardSelection,
    setShowGraveyardViewer,
    setPendingDeckReview,
    setPendingMonsterTarget,

    // Utilities
    addLog,
    dispatch,
  });

  // Phase 6-2: 召喚・チャージ Hook
  const { summonCard, chargeCard, chargeSP, chargePhaseCard } = useSummon({
    // ゲーム状態（読み取り）
    currentPlayer,
    summoningSickness,
    chargeUsedThisTurn,

    // プレイヤー状態（読み取り）
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1Life,
    p2Life,
    p1ActiveSP,
    p2ActiveSP,
    p1RestedSP,
    p2RestedSP,
    p1BonusSP,
    p2BonusSP,
    p1StatusEffects,
    p2StatusEffects,
    p1PhaseCard,
    p2PhaseCard,
    p1MagicBlocked,
    p2MagicBlocked,

    // 深海領域
    p1DeepSeaZone: deepSea.p1DeepSeaZone,
    p2DeepSeaZone: deepSea.p2DeepSeaZone,

    // Setters
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    setP1ActiveSP,
    setP2ActiveSP,
    setP1RestedSP,
    setP2RestedSP,
    setP1BonusSP,
    setP2BonusSP,
    setP1FieldCard,
    setP2FieldCard,
    setP1PhaseCard,
    setP2PhaseCard,
    setP1NextTurnSPBonus,
    setP2NextTurnSPBonus,
    setChargeUsedThisTurn,
    setP1DeepSeaZone: deepSea.setP1DeepSeaZone,
    setP2DeepSeaZone: deepSea.setP2DeepSeaZone,

    // UI状態Setters
    setPendingMonsterTarget,
    setPendingDeckReview,
    setPendingGraveyardSelection,
    setPendingHandSelection,
    setShowGraveyardViewer,

    // ユーティリティ
    addLog,
    dispatch,
  });

  // Phase 6-3: 攻撃処理 Hook
  const { executeAttack, attack } = useCombat({
    // ゲーム状態（読み取り）
    currentPlayer,
    isFirstTurn,
    firstPlayer,
    chainConfirmation,
    loneWarriorRule,
    piercingDamage,

    // プレイヤー状態（読み取り）
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1FieldCard,
    p2FieldCard,
    p1Life,
    p2Life,
    p1StatusEffects,
    p2StatusEffects,

    // Setters
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    setP1FieldCard,
    setP2FieldCard,
    setP1StatusEffects,
    setP2StatusEffects,
    setAttackingMonster,
    setSelectedFieldMonster,

    // 関数
    startChainConfirmation,
    addLog,
  });

  // Phase 6-4: スキル発動 Hook
  const { executeSkill } = useSkillExecution({
    // ゲーム状態（読み取り）
    currentPlayer,
    p1Life,
    p2Life,
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1ActiveSP,
    p2ActiveSP,
    p1RestedSP,
    p2RestedSP,
    p1FieldCard,
    p2FieldCard,
    p1StatusEffects,
    p2StatusEffects,

    // Setters
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    setP1ActiveSP,
    setP2ActiveSP,
    setP1RestedSP,
    setP2RestedSP,
    setP1FieldCard,
    setP2FieldCard,
    setP1MagicBlocked,
    setP2MagicBlocked,
    setP1StatusEffects,
    setP2StatusEffects,
    setPendingMonsterTarget,
    setPendingHandSelection,

    // ユーティリティ
    addLog,
  });

  // 次のフェイズへ（useAIControllerより前に定義が必要）
  const nextPhase = useCallback(() => {
    // 手札選択待ち中はフェイズ進行不可
    if (pendingHandSelection) {
      addLog('手札を選択してください', 'damage');
      return;
    }
    // チェーン確認中はフェイズ進行不可
    if (chainConfirmation) {
      addLog('チェーン確認中です', 'info');
      return;
    }
    if (phase === 2) {
      // 先攻1ターン目はバトルフェイズをスキップ（攻撃不可のため）
      if (isFirstTurn && currentPlayer === firstPlayer) {
        dispatch(gameActions.setPhase(4));
        setSelectedHandCard(null);
        processPhase(4);
        return;
      }

      // バトルフェイズ開始時のチェーンポイント
      const needsConfirmation = startChainConfirmation(
        CHAIN_POINTS.BATTLE_START,
        { type: 'battleStart' },
        {}
      );

      if (!needsConfirmation) {
        // チェーン確認不要 → 直接バトルフェイズへ
        // Phase D-1: dispatchのみで状態更新
        dispatch(gameActions.setPhase(3));
        setSelectedHandCard(null);
        // バトルフェイズ開始時トリガーを発火
        processPhase(3);
      }
      // needsConfirmation === true の場合、確認後にproceedToBattlePhaseが呼ばれる
    } else if (phase === 3) {
      // Phase D-1: dispatchのみで状態更新
      dispatch(gameActions.setPhase(4));
      processPhase(4);
    }
  }, [pendingHandSelection, chainConfirmation, phase, isFirstTurn, currentPlayer, firstPlayer, startChainConfirmation, dispatch, setSelectedHandCard, processPhase, addLog]);

  // Phase 7: AI制御 Hook
  const { resetAI } = useAIController({
    // ゲーム状態
    gameState,
    phase,
    currentPlayer,
    turn,
    isFirstTurn,

    // プレイヤータイプ・難易度
    p1PlayerType,
    p2PlayerType,
    p1AIDifficulty,
    p2AIDifficulty,

    // ライフ
    p1Life,
    p2Life,

    // 手札
    p1Hand,
    p2Hand,

    // フィールド
    p1Field,
    p2Field,

    // 墓地
    p1Graveyard,
    p2Graveyard,

    // デッキ
    p1Deck,
    p2Deck,

    // SP
    p1ActiveSP,
    p2ActiveSP,
    p1RestedSP,
    p2RestedSP,
    p1BonusSP,
    p2BonusSP,

    // フィールドカード・フェイズカード
    p1FieldCard,
    p2FieldCard,
    p1PhaseCard,
    p2PhaseCard,

    // 選択状態
    pendingHandSelection,
    setPendingHandSelection,
    pendingSelectedCard,
    setPendingSelectedCard,
    pendingMonsterTarget,
    setPendingMonsterTarget,
    pendingSelectedMonsterIndex,
    setPendingSelectedMonsterIndex,
    pendingGraveyardSelection,
    setPendingGraveyardSelection,
    pendingGraveyardSelectedCard,
    setPendingGraveyardSelectedCard,
    pendingDeckReview,
    setPendingDeckReview,

    // チェーン確認
    chainConfirmation,

    // バトルアクション
    summonCard,
    chargeCard,
    attack,
    executeSkill,
    nextPhase,

    // 刹那詠唱
    handleSkipChainConfirmation,
    activateSetsunaInChain,

    // チャージ使用フラグ
    chargeUsedThisTurn,

    // ログ
    addLog,

    // AI思考速度取得関数
    getScaledDelay,
  });

  // 効果コンテキスト作成（dispatch経由のセッターを含む）
  const createEffectContext = useCallback((overrides = {}) => {
    // 読み取り専用データ
    const readonlyContext = {
      currentPlayer,
      p1Life, p2Life,
      p1Field, p2Field,
      p1Hand, p2Hand,
      p1Deck, p2Deck,
      p1Graveyard, p2Graveyard,
      p1ActiveSP, p2ActiveSP,
      p1RestedSP, p2RestedSP,
      p1FieldCard, p2FieldCard,
      p1PhaseCard, p2PhaseCard,
      p1StatusEffects, p2StatusEffects,
      p1NextTurnSPBonus, p2NextTurnSPBonus,
      p1MagicBlocked, p2MagicBlocked,
      p1SpReduction, p2SpReduction,
      // ゲーム設定
      summoningSickness,
    };

    // dispatch経由のセッター（上で定義したuseCallback版を使用）
    const dispatchSetters = {
      setP1Life, setP2Life,
      setP1Field, setP2Field,
      setP1Hand, setP2Hand,
      setP1Deck, setP2Deck,
      setP1Graveyard, setP2Graveyard,
      setP1ActiveSP, setP2ActiveSP,
      setP1RestedSP, setP2RestedSP,
      setP1FieldCard, setP2FieldCard,
      setP1PhaseCard, setP2PhaseCard,
      setP1StatusEffects, setP2StatusEffects,
      setP1NextTurnSPBonus, setP2NextTurnSPBonus,
      setP1MagicBlocked, setP2MagicBlocked,
      setP1SpReduction, setP2SpReduction,
      // ゲームフラグ設定（遅延効果用）
      setGameFlags,
      // ログ（既にdispatch経由）
      addLog,
      // UI制御（これらはuseStateのまま - 純粋にUI関連）
      setPendingMonsterTarget,
      setPendingHandSelection,
    };

    return { ...readonlyContext, ...dispatchSetters, ...overrides };
  }, [
    addLog,
    setP1Life, setP2Life, setP1Field, setP2Field, setP1Hand, setP2Hand,
    setP1Deck, setP2Deck, setP1Graveyard, setP2Graveyard,
    setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
    setP1FieldCard, setP2FieldCard, setP1PhaseCard, setP2PhaseCard,
    setP1StatusEffects, setP2StatusEffects, setP1NextTurnSPBonus, setP2NextTurnSPBonus,
    setP1MagicBlocked, setP2MagicBlocked, setP1SpReduction, setP2SpReduction,
    setGameFlags,
    currentPlayer,
    p1Life, p2Life, p1Field, p2Field, p1Hand, p2Hand, p1Deck, p2Deck,
    p1Graveyard, p2Graveyard, p1ActiveSP, p2ActiveSP, p1RestedSP, p2RestedSP,
    p1FieldCard, p2FieldCard, p1PhaseCard, p2PhaseCard,
    p1StatusEffects, p2StatusEffects, p1NextTurnSPBonus, p2NextTurnSPBonus,
    p1MagicBlocked, p2MagicBlocked, p1SpReduction, p2SpReduction,
    setPendingMonsterTarget, setPendingHandSelection,
    summoningSickness,
  ]);

  // カードのコスト修正情報を取得（手札表示用）
  const getModifiedCostInfo = useCallback((card, player) => {
    const context = {
      currentPlayer: player,
      effectOwner: player,
      p1Field,
      p2Field,
      p1Life,
      p2Life,
    };

    // 魔法カードの場合（エリザヴェットの効果など）
    if (card.type === 'magic') {
      const magicModifier = continuousEffectEngine.calculateMagicCostModifier(card, player, context);

      // C0000552: 海を舐めた末路 - 深海領域のカード枚数分コスト軽減
      let deepSeaReduction = 0;
      if (card.id === 'C0000552') {
        const playerDeepSeaZone = player === 1 ? deepSea.p1DeepSeaZone : deepSea.p2DeepSeaZone;
        if (playerDeepSeaZone && playerDeepSeaZone.isUnlocked) {
          deepSeaReduction = playerDeepSeaZone.cards.length;
        }
      }

      const totalModifier = magicModifier - deepSeaReduction;

      if (totalModifier === 0) {
        return { modifiedCost: undefined, costModifierSource: undefined };
      }
      const actualCost = Math.max(0, card.cost + totalModifier);
      const sources: string[] = [];
      if (deepSeaReduction > 0) {
        sources.push('深海領域');
      }
      if (magicModifier !== 0) {
        sources.push('効果');
      }
      return { modifiedCost: actualCost, costModifierSource: sources.length > 0 ? sources.join(', ') : '効果' };
    }

    // モンスターカード以外はここで終了
    if (card.type !== 'monster') {
      return { modifiedCost: undefined, costModifierSource: undefined };
    }

    // 常時効果からのコスト修正（モンスターカード用）
    const { modifier, sources } = continuousEffectEngine.getSummonCostModifierDetails(card, player, context);

    // カード固有の一時的コスト修正（潮の乙女など）
    const tempModifier = card.tempCostModifier || 0;
    const tempSource = card.tempCostModifierSource || null;

    // プレイヤー状態異常からのコスト軽減（クリスタルブレス等）
    const playerStatusEffects = player === 1 ? p1StatusEffects : p2StatusEffects;
    const statusCostReduction = statusEffectEngine.getSummonCostReduction(playerStatusEffects, card.attribute);

    const totalModifier = modifier + tempModifier - statusCostReduction;

    if (totalModifier === 0) {
      return { modifiedCost: undefined, costModifierSource: undefined };
    }

    const actualCost = calculateSummonCost(card.cost, modifier, tempModifier, statusCostReduction);
    const allSources = [...sources];
    if (tempSource) {
      allSources.push(tempSource);
    }
    if (statusCostReduction > 0) {
      allSources.push('光輝の祝福');
    }
    const sourceText = allSources.length > 0 ? allSources.join(', ') : '効果';

    return { modifiedCost: actualCost, costModifierSource: sourceText };
  }, [p1Field, p2Field, p1Life, p2Life, p1StatusEffects, p2StatusEffects, deepSea.p1DeepSeaZone, deepSea.p2DeepSeaZone]);

  // ========================================
  // Phase D-4: useState→engineState同期は不要になった
  // 全プレイヤー状態はengineStateから直接参照
  // セッターはdispatch経由で更新
  // ========================================


  // CSVファイルの読み込み & プレイヤーデータ初期化
  useEffect(() => {
    const loadCards = async () => {
      setIsLoadingCards(true);
      const cards = await loadCardsFromCSV();
      setAllCards(cards);

      // カード価値マップを計算
      const valueMap = valueCalculator.calculateAllCardValues(cards);
      setCardValueMap(valueMap);

      // プレイヤーデータをロード or 新規作成
      const loaded = storage.loadResult();
      if (loaded.status !== 'ok' && loaded.status !== 'missing') {
        setPersistenceIssue({ message: loaded.message, data: null });
        setIsLoadingCards(false);
        return;
      }
      let data = loaded.status === 'ok' ? loaded.data : createInitialPlayerData();
      const validation = validatePlayerData(data);
      if (loaded.status === 'missing' || !validation.valid) {
        if (!validation.valid) data = repairPlayerData(data);
        if (!storage.save(data, { backup: loaded.status === 'ok' })) {
          setPersistenceIssue({ message: '初期化・修復したデータを保存できませんでした。進行を始める前にデータを退避してください。', data });
          setIsLoadingCards(false);
          return;
        }
      }
      setPlayerData(data);
      setIsLoadingCards(false);
    };

    loadCards().catch(error => {
      console.error('初期化に失敗しました:', error);
      setPersistenceIssue({ message: 'カード・セーブデータの初期化に失敗しました。保存内容は自動初期化しません。', data: null });
      setIsLoadingCards(false);
    });
  }, []);

  // ========================================
  // サーバー市場価格をplayerData.marketに同期
  // ========================================
  useEffect(() => {
    // サーバー価格が取得できていない、またはplayerDataがまだない場合はスキップ
    if (!serverPrices || Object.keys(serverPrices).length === 0 || !playerData) {
      return;
    }

    // サーバー価格をplayerData.marketに反映
    setPlayerData(prev => {
      if (!prev) return prev;

      const updatedMarket = {
        ...prev.market,
        cardPrices: serverPrices,                     // サーバーのカード価格
        serverIndexes: serverIndexes,                 // サーバーの属性指数
        serverNews: serverNews,                       // サーバーのニュース
        serverChainNews: serverChainNews,             // サーバーの連鎖ニュース
        serverNewsHistory: serverNewsHistory,         // サーバーのニュース履歴
        serverActiveTrend: serverActiveTrend,         // サーバーの週間トレンド
        serverSpotlightEvent: serverSpotlightEvent,   // サーバーのスポットライト
        serverSuddenEvent: serverSuddenEvent,         // サーバーの突発イベント
        serverOvernightVolatility: serverOvernightVolatility,  // サーバーの夜間変動
        serverTimestamp: marketTimestamp,             // 最終更新時刻
        isServerData: true,                           // サーバーデータフラグ
        // サーバーの永続変動を反映（新規ユーザーでも表示されるように）
        ...(serverPersistentModifiers && { persistentModifiers: serverPersistentModifiers }),
      };

      const newData = {
        ...prev,
        market: updatedMarket,
      };

      // ローカルストレージには保存しない（サーバーデータなので）
      // storage.save(newData); は呼ばない
      return newData;
    });
  }, [serverPrices, serverIndexes, serverNews, serverChainNews, serverNewsHistory, serverActiveTrend, serverSpotlightEvent, serverSuddenEvent, serverOvernightVolatility, serverPersistentModifiers, marketTimestamp, playerData?.market?.currentDay]);

  // ========================================
  // サーバー価格更新時にリアルタイムで価格履歴を記録
  // ========================================
  const lastRecordedMarketTimestampRef = useRef<Date | null>(null);

  useEffect(() => {
    // 必要なデータが揃っていない場合はスキップ
    if (!marketTimestamp || !serverPrices || Object.keys(serverPrices).length === 0 || !playerData || !allCards) {
      return;
    }

    // 同じタイムスタンプで既に記録済みの場合はスキップ
    if (lastRecordedMarketTimestampRef.current?.getTime() === marketTimestamp.getTime()) {
      return;
    }

    // 初回ロード時（refがnull）で既に履歴がある場合は記録をスキップ
    // （ページリロード時に重複記録を防ぐ）
    const currentHistory = playerData.market?.priceHistory?.marketIndex || [];
    if (lastRecordedMarketTimestampRef.current === null && currentHistory.length > 0) {
      lastRecordedMarketTimestampRef.current = marketTimestamp;
      return;
    }

    console.log('[Market] Recording price history on server update:', marketTimestamp);

    // 価格履歴を記録
    const getBaseValue = (card) => {
      const cardValue = cardValueMap?.get?.(card.id);
      if (cardValue) {
        return cardValue.baseValue;
      }
      return valueCalculator.calculateBaseValue(card);
    };
    const getTier = (card) => {
      const cardValue = cardValueMap?.get?.(card.id);
      if (cardValue) {
        return cardValue.tier;
      }
      const baseValue = valueCalculator.calculateBaseValue(card);
      return valueCalculator.determineTier(baseValue);
    };
    const getMarketModifier = (card, tier) => {
      return calculateMarketModifier(card, playerData.market, null, tier);
    };

    // 現在の市場状態にサーバー価格を含めて履歴記録
    const marketStateWithServerPrices = {
      ...playerData.market,
      cardPrices: serverPrices,
    };

    const newPriceHistory = recordPriceHistory(
      playerData.market?.priceHistory,
      marketStateWithServerPrices,
      allCards,
      getBaseValue,
      getTier,
      getMarketModifier
    );

    // playerDataを更新
    setPlayerData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        market: {
          ...prev.market,
          priceHistory: newPriceHistory,
        },
      };
    });

    // 記録済みタイムスタンプを更新
    lastRecordedMarketTimestampRef.current = marketTimestamp;
  }, [marketTimestamp, serverPrices, playerData, allCards, cardValueMap]);

  // ========================================
  // 認証後のサーバーデータ同期
  // ========================================

  // useAuthから取得したserverPlayerDataが変わったらplayerDataに反映
  // 注意: useAuthの初期化時に自動でサーバーからデータを取得するため、
  //       別途APIを呼び出す必要はない
  useEffect(() => {
    const receive = (event) => {
      setPlayerData(previous => {
        if (!previous) return previous;
        const updated = applyEconomySnapshot(previous, event.detail, economyAccount());
        if (updated !== previous && !storage.save(updated)) {
          setPersistenceIssue({ message: 'サーバーの取引結果を端末に保存できませんでした。', data: updated });
        }
        return updated;
      });
    };
    window.addEventListener(ECONOMY_EVENT, receive);
    return () => window.removeEventListener(ECONOMY_EVENT, receive);
  }, []);

  useEffect(() => {
    const snapshot = authState.serverPlayerData?.snapshot;
    if (!snapshot || !playerData) return;
    setPlayerData(previous => {
      const updated = applyEconomySnapshot(previous, snapshot, economyAccount());
      if (updated !== previous) storage.save(updated);
      return updated;
    });
  }, [authState.serverPlayerData, !!playerData]);

  // ========================================
  // コレクションシステム ハンドラ
  // ========================================

  // プレイヤーデータを更新して保存
  // 関数形式 (prev) => newData とオブジェクト形式の両方に対応
  const updatePlayerData = useCallback((newDataOrUpdater) => {
    setPlayerData((prev) => {
      const candidate = typeof newDataOrUpdater === 'function'
        ? newDataOrUpdater(prev)
        : newDataOrUpdater;
      const newData = preserveOnlineAssets(prev, candidate, !!economyAccount());
      if (!storage.save(newData)) {
        setPersistenceIssue({ message: '進行状況を保存できませんでした。現在の進行を退避してから保存領域を確認してください。', data: newData });
      }
      return newData;
    });
  }, []);

  // 対戦報酬を付与
  // 日付進行の共通処理（市場更新、価格履歴、資産スナップショット、大会処理）
  const processDayAdvancement = useCallback(async (inputPlayerData) => {
    if (economyAccount()) {
      const settlement = await economyRequest('investment/process-day', {});
      if (settlement.seizure?.remaining_debt > 0) { setAutoBattleInitialDebt(settlement.seizure.remaining_debt); setGameState('autoBattleLoop'); }
      const current = await economyRequest('tournament/current');
      let tournament = current.tournament;
      if (tournament?.status === 'closed') {
        const resolved = await economyRequest('tournament/resolve', { tournament_id: tournament.id });
        tournament = resolved.tournament;
        if (resolved.player?.tournament_data?.currentBets?.length) {
          setPendingTournamentResult(tournament);
          setShowTournamentViewer(true);
        } else {
          await economyRequest('tournament/payouts/claim', { tournament_id: tournament.id });
        }
      }
      return { ...inputPlayerData, market: { ...inputPlayerData.market,
        currentDay: (inputPlayerData.market?.currentDay ?? 0) + 1 } };
    }
    let updatedPlayerData = { ...inputPlayerData };

    if (!updatedPlayerData.market) {
      return updatedPlayerData;
    }

    // 市場日を進める（allCardsを渡してスポットライト生成を可能にする）
    const newMarketState = advanceDay(updatedPlayerData.market, allCards || []);

    // 価格履歴を記録
    const getBaseValue = (card) => {
      const cardValue = cardValueMap?.get?.(card.id);
      if (cardValue) {
        return cardValue.baseValue;
      }
      return valueCalculator.calculateBaseValue(card);
    };
    const getTier = (card) => {
      const cardValue = cardValueMap?.get?.(card.id);
      if (cardValue) {
        return cardValue.tier;
      }
      const baseValue = valueCalculator.calculateBaseValue(card);
      return valueCalculator.determineTier(baseValue);
    };
    const getMarketModifier = (card, tier) => {
      return calculateMarketModifier(card, newMarketState, null, tier);
    };

    const newPriceHistory = recordPriceHistory(
      newMarketState.priceHistory,
      newMarketState,
      allCards || [],
      getBaseValue,
      getTier,
      getMarketModifier
    );

    updatedPlayerData = {
      ...updatedPlayerData,
      market: {
        ...newMarketState,
        priceHistory: newPriceHistory,
      },
    };

    // 投資ファンド価格を更新（カード価格から計算）
    if (updatedPlayerData.investmentData) {
      // マーケットインデックスを構築（カード価格からファンド・FX価格を計算するため）
      const marketIndex = buildMarketIndex(allCards || []);

      // ファンド価格履歴を記録（カード価格から計算された価格を履歴に保存）
      updatedPlayerData = {
        ...updatedPlayerData,
        investmentData: recordDailyFundPrices(
          updatedPlayerData.investmentData,
          marketIndex,
          newMarketState
        ),
      };

      // FXレート履歴を記録（属性指数から計算されたレートを履歴に保存）
      updatedPlayerData = {
        ...updatedPlayerData,
        investmentData: recordDailyFXRates(
          updatedPlayerData.investmentData,
          marketIndex,
          newMarketState
        ),
      };

      // 投資日次処理（ロスカット、自動注文、スワップコスト、借金利息）
      const currentBattleNumber = newMarketState.currentDay;
      let investData = updatedPlayerData.investmentData;
      let currentGold = updatedPlayerData.gold;
      let totalDebtGenerated = 0;

      // 投資処理結果を収集（UI通知用）
      const investmentResults = {
        autoOrders: [],
        fundLiquidations: [],
        fxLiquidations: [],
        swapCost: 0,
        maintenanceCost: 0,
        debtGenerated: 0,
        interestAdded: 0,
      };

      // 1. FXスワップコスト適用
      const swapResult = applyDailySwap(investData);
      investData = swapResult.newInvestmentData;
      if (swapResult.totalSwap > 0) {
        console.log(`[Investment] FXスワップコスト: ${swapResult.totalSwap}G`);
        investmentResults.swapCost = swapResult.totalSwap;
      }

      // 1.5. ファンド維持コスト適用
      const maintenanceResult = applyMaintenanceCost(investData, currentGold, marketIndex, newMarketState);
      investData = maintenanceResult.newInvestmentData;
      currentGold = maintenanceResult.newGold;
      if (maintenanceResult.maintenanceCost > 0) {
        console.log(`[Investment] ファンド維持コスト: ${maintenanceResult.maintenanceCost}G`);
        investmentResults.maintenanceCost = maintenanceResult.maintenanceCost;
      }

      // 2. ファンド自動注文処理（ストップロス/テイクプロフィット）
      const autoOrderResult = processAutoOrders(investData, currentBattleNumber, marketIndex, newMarketState);
      investData = autoOrderResult.newInvestmentData;
      if (autoOrderResult.executedOrders.length > 0) {
        console.log(`[Investment] 自動注文実行: ${autoOrderResult.executedOrders.length}件`);
        for (const order of autoOrderResult.executedOrders) {
          console.log(`  - ${order.type === 'stop_loss' ? 'SL発動' : 'TP発動'}: ${order.fundId}, PnL: ${order.pnl}G`);
        }
        investmentResults.autoOrders = autoOrderResult.executedOrders;
        // 利益/返還金を所持金に追加
        if (autoOrderResult.totalPnl > 0) {
          currentGold += autoOrderResult.totalPnl;
        }
      }

      // 3. ファンドレバレッジロスカット
      const fundLiqResult = processLeverageLiquidations(investData, currentBattleNumber, marketIndex, newMarketState);
      investData = fundLiqResult.newInvestmentData;
      if (fundLiqResult.liquidatedItems.length > 0) {
        console.log(`[Investment] ファンドロスカット: ${fundLiqResult.liquidatedItems.length}件`);
        for (const item of fundLiqResult.liquidatedItems) {
          console.log(`  - ${item.fundId} (${item.leverage}倍レバ): 維持率${item.marginRatio}%`);
        }
        investmentResults.fundLiquidations = fundLiqResult.liquidatedItems;
        totalDebtGenerated += fundLiqResult.debtCreated || 0;
      }

      // 4. FXロスカット（夜間変動考慮）
      const overnightVolatility = newMarketState.overnightVolatility || null;
      const fxLiqResult = processLiquidations(investData, currentBattleNumber, marketIndex, newMarketState, overnightVolatility);
      investData = fxLiqResult.newInvestmentData;
      if (fxLiqResult.liquidatedPositions.length > 0) {
        console.log(`[Investment] FXロスカット: ${fxLiqResult.liquidatedPositions.length}件`);
        for (const pos of fxLiqResult.liquidatedPositions) {
          console.log(`  - ${pos.pairId} (${pos.leverage}倍レバ): PnL ${pos.pnl}G`);
        }
        investmentResults.fxLiquidations = fxLiqResult.liquidatedPositions;
        totalDebtGenerated += fxLiqResult.debtGenerated || 0;
      }
      // 夜間変動によるロスカット記録
      if (fxLiqResult.overnightLiquidations && fxLiqResult.overnightLiquidations.length > 0) {
        console.log(`[Investment] 夜間変動によるロスカット: ${fxLiqResult.overnightLiquidations.length}件`);
        for (const pos of fxLiqResult.overnightLiquidations) {
          console.log(`  - ${pos.pairId}: 夜間${pos.spikeDirection === 'up' ? '急騰' : '急落'}${Math.abs(pos.spikePercent)}%`);
        }
        investmentResults.overnightLiquidations = fxLiqResult.overnightLiquidations;
      }

      // 5. 借金発生処理
      if (totalDebtGenerated > 0) {
        console.log(`[Investment] 借金発生: ${totalDebtGenerated}G`);
        investmentResults.debtGenerated = totalDebtGenerated;
        if (hasDebt(investData.debt)) {
          investData = {
            ...investData,
            debt: addToDebt(investData.debt, totalDebtGenerated, currentBattleNumber),
          };
        } else {
          investData = {
            ...investData,
            debt: createDebt(totalDebtGenerated, currentBattleNumber, 'leverage'),
          };
        }
      }

      // 6. 借金利息処理
      if (hasDebt(investData.debt)) {
        const interestResult = applyDailyInterest(investData.debt, currentBattleNumber);
        investData = {
          ...investData,
          debt: interestResult.newDebtData,
        };
        if (interestResult.interestAdded > 0) {
          console.log(`[Investment] 借金利息: +${interestResult.interestAdded}G (総額: ${interestResult.newDebtData.amount}G)`);
          investmentResults.interestAdded = interestResult.interestAdded;
        }
      }

      // 7. 配当処理（長期保有ボーナス）
      const dividendResult = processDividends(investData, currentBattleNumber, marketIndex, newMarketState);
      investData = dividendResult.newInvestmentData;
      if (dividendResult.totalDividend > 0) {
        console.log(`[Investment] 配当金受取: +${dividendResult.totalDividend}G (${dividendResult.dividendDetails.length}件)`);
        for (const detail of dividendResult.dividendDetails) {
          console.log(`  - ${detail.fundId}: ${detail.units}口 × ${(detail.rate * 100).toFixed(1)}% = ${detail.dividend}G (${detail.holdingDays}日保有)`);
        }
        currentGold += dividendResult.totalDividend;
        investmentResults.dividends = dividendResult.dividendDetails;
        investmentResults.totalDividend = dividendResult.totalDividend;
      }

      // 投資データと所持金を更新
      updatedPlayerData = {
        ...updatedPlayerData,
        investmentData: investData,
        gold: currentGold,
      };

      // 投資処理結果を保存（後でreturnに含める）
      updatedPlayerData._investmentResults = investmentResults;

      // 7. 借金期限切れチェック（差し押さえトリガー）
      if (hasDebt(investData.debt) && isDebtOverdue(investData.debt, currentBattleNumber)) {
        console.log(`[Investment] 借金期限切れ！差し押さえが必要: ${investData.debt.amount}G`);
        updatedPlayerData._seizureNeeded = true;
      }

      // 8. サーバーに日経過処理を送信し、結果でゴールドを同期
      const serverResult = await processInvestmentDayOnServer(currentBattleNumber);
      if (serverResult.success) {
        console.log('[Investment] Day processing saved to server:', serverResult);
        // サーバーの結果でゴールドを上書き（Single Source of Truth）
        if (serverResult.new_gold_balance !== undefined) {
          updatedPlayerData = {
            ...updatedPlayerData,
            gold: serverResult.new_gold_balance,
          };
        }
      } else if (serverResult.error !== 'Not authenticated') {
        console.warn('[Investment] Server day processing failed:', serverResult.error);
        // サーバー失敗時はローカルのゴールドを維持
      }
    }

    // 資産スナップショットを記録（ファンド・FX評価額も含める）
    const assetMarketIndex = buildMarketIndex(allCards || []);
    updatedPlayerData = recordAssetSnapshot(
      updatedPlayerData,
      allCards || [],
      newMarketState,
      newMarketState.currentDay,
      assetMarketIndex
    );

    // 大会システム処理
    const currentDay = newMarketState.currentDay;
    let existingTournament = updatedPlayerData.tournamentData?.currentTournament;

    // 1. 既存大会のステータス更新（締切チェック）
    if (existingTournament && existingTournament.status === TOURNAMENT_STATUS.BETTING) {
      const previousStatus = existingTournament.status;
      existingTournament = updateTournamentStatus(existingTournament, currentDay);

      // ステータスがBETTING→CLOSEDに変わった場合、サーバーに賭けを確定送信
      if (previousStatus === TOURNAMENT_STATUS.BETTING &&
          existingTournament.status === TOURNAMENT_STATUS.CLOSED) {
        const currentBets = updatedPlayerData?.tournamentData?.currentBets || [];
        if (currentBets.length > 0 && isBettingServerAvailable()) {
          const serverBets = convertLocalBetsToServerFormat(currentBets);
          confirmBetsOnServer(
            existingTournament.id,
            existingTournament.type,
            serverBets
          ).then((result) => {
            if (result.success) {
              console.log('[Tournament] 賭けをサーバーに確定:', result);
              // サーバーのゴールド残高で更新（ローカルは既に減算済み）
              if (result.new_gold_balance !== undefined) {
                updatePlayerData((prev) => ({
                  ...prev,
                  gold: result.new_gold_balance,
                }));
              }
            } else {
              console.warn('[Tournament] サーバー確定失敗（ローカルのみで続行）:', result.error);
            }
          }).catch((err) => {
            console.error('[Tournament] サーバー確定エラー:', err);
          });
        }
      }

      updatedPlayerData = {
        ...updatedPlayerData,
        tournamentData: {
          ...updatedPlayerData.tournamentData,
          currentTournament: existingTournament,
        },
      };
    }

    // 2. CLOSED状態の大会を実行
    if (existingTournament && existingTournament.status === TOURNAMENT_STATUS.CLOSED) {
      const currentBets = updatedPlayerData?.tournamentData?.currentBets || [];
      const hasParticipated = currentBets.length > 0;

      runTournament(existingTournament).then((finishedTournament) => {
        if (finishedTournament) {
          console.log(`[Tournament] ${finishedTournament.name} 終了: 優勝 ${finishedTournament.finalWinner}`);

          if (hasParticipated) {
            // 参加していた場合 → 観戦ダイアログを表示
            updatePlayerData((prev) => ({
              ...prev,
              tournamentData: {
                ...prev.tournamentData,
                currentTournament: finishedTournament,
              },
            }));
            setPendingTournamentResult(finishedTournament);
            setShowTournamentViewer(true);
          } else {
            // 参加していない場合 → 履歴のみ記録して大会クリア（ダイアログなし）
            const emptyPayoutResult = {
              bets: [],
              totalBet: 0,
              totalPayout: 0,
              totalProfit: 0,
              totalCount: 0,
              wonCount: 0,
            };
            const historyEntry = createHistoryEntry(finishedTournament, [], emptyPayoutResult);

            updatePlayerData((prev) => ({
              ...prev,
              tournamentData: {
                ...prev.tournamentData,
                currentTournament: null,
                currentBets: [],
                purchasedInfo: {},
                history: [historyEntry, ...(prev.tournamentData?.history || [])].slice(0, 500),
              },
            }));
            console.log(`[Tournament] ${finishedTournament.name} 終了（参加なし、履歴のみ記録）`);
          }
        }
      }).catch((err) => {
        console.error('[Tournament] 大会実行エラー:', err);
      });
    } else {
      // 既存大会がない or FINISHED の場合、新規大会チェック
      const triggerType = checkTournamentTrigger(currentDay, existingTournament);
      if (triggerType) {
        createTournament(triggerType, currentDay).then((newTournament) => {
          if (newTournament) {
            updatePlayerData((prev) => ({
              ...prev,
              tournamentData: {
                ...prev.tournamentData,
                currentTournament: newTournament,
              },
            }));
            console.log(`[Tournament] ${newTournament.name} が開催されました`);
          }
        }).catch((err) => {
          console.error('[Tournament] 大会作成エラー:', err);
        });
      }
    }

    // 商人の品揃えを生成（ウィッシュリスト通知用）
    // cardValueMapが存在しない場合は正確な価格計算ができないためスキップ
    if (allCards && allCards.length > 0 && cardValueMap) {
      const newDayId = newMarketState.currentDay;
      const merchantData = updatedPlayerData.merchantData || {};
      const forbiddenCount = countForbiddenCards(updatedPlayerData, allCards);
      const totalAssets = updatedPlayerData.gold || 0; // 簡易計算

      // 今日の出現商人を取得（出現情報もキャッシュに保存）
      const { appearances, updated: appearancesUpdated, newMerchantData } = getTodayAppearances(merchantData, newDayId, {
        forbiddenCount,
        totalAssets,
      });

      // 出現情報が更新された場合は newMerchantData を使う（todayAppearances が含まれる）
      const baseMerchantData = appearancesUpdated && newMerchantData ? newMerchantData : merchantData;

      // 価格計算関数を作成（MerchantShop.jsxと同じロジック）
      const getMarketPriceForStock = (card, rarity) => {
        const valueInfo = cardValueMap.get(card.id);
        if (!valueInfo) return 100;
        const baseValue = valueInfo.rarityValues?.[rarity] || valueInfo.baseValue || 100;
        const result = getCardMarketPrice(card, baseValue, newMarketState, rarity, valueInfo.tier);
        return result?.price || baseValue;
      };

      // 全商人の品揃えを生成
      const stocks = {};
      const allMerchantNames = [
        'マルクス',
        ...(appearances.attribute || []),
        ...(appearances.collector || []),
        ...(appearances.dark ? ['名無し'] : []),
        ...(appearances.traveler ? ['ゼルヴァン'] : []),
      ];

      for (const merchantName of allMerchantNames) {
        const merchant = MERCHANTS[merchantName];
        if (!merchant) continue;

        const favLevel = baseMerchantData.favorability?.[merchantName]?.level || 1;
        const stock = generateStock(merchant, allCards, newDayId, {
          favorabilityLevel: favLevel,
          getMarketPrice: getMarketPriceForStock,
        });
        stocks[merchantName] = stock;
      }

      if (Object.keys(stocks).length > 0) {
        updatedPlayerData = {
          ...updatedPlayerData,
          merchantData: {
            ...baseMerchantData,
            todayStock: {
              dayId: newDayId,
              stocks,
            },
          },
        };
      }
    }

    return updatedPlayerData;
  }, [cardValueMap, allCards, updatePlayerData]);

  // 対戦報酬付与＋日付進行
  const awardBattleRewards = useCallback(async (isWin) => {
    if (!playerData) return;
    if (economyAccount()) {
      if (adventureBattleContext) {
        const { reward } = await adventureBattleContext.onBattleEnd(adventureBattleContext.npcId, isWin ? 'win' : 'lose', playerData);
        setBattleReward({ goldReward: reward.gold, packReward: reward.packs.reduce((sum, p) => sum + p.count, 0), isWin,
          rewardLimited: reward.rewardLimited });
      } else {
        const result = await economyRequest('collection/battle-reward', { battle_id: pendingBattleId('normal'), is_win: isWin });
        setBattleReward({ goldReward: result.gold_reward, packReward: result.pack_reward, isWin,
          rewardLimited: result.reward_limited });
        try { await processDayAdvancement(playerData); }
        catch (error) { alert(`対戦報酬は保存済みです。日次処理の更新: ${error.message}`); }
      }
      return;
    }


    // ========================================
    // サーバーへ報酬保存（認証済みの場合のみ）
    // サーバーの結果を待ち、ゴールドはサーバーの値を使用
    // ========================================
    const serverResult = await saveBattleRewardToServer(isWin);

    let goldReward = 0;
    let packReward = 0;
    let updatedGold = playerData.gold;

    if (serverResult.success) {
      console.log('[awardBattleRewards] Server save successful:', serverResult);
      goldReward = serverResult.goldReward;
      packReward = serverResult.packReward;
      updatedGold = serverResult.newGoldBalance;
    } else if (serverResult.error !== 'Not authenticated') {
      console.warn('[awardBattleRewards] Server save failed:', serverResult.error);
      // サーバー失敗時はゴールドを増やさない
    }

    // 報酬を適用したプレイヤーデータを作成
    let updatedPlayerDataWithReward = {
      ...playerData,
      gold: updatedGold,
    };

    // パック報酬がある場合は未開封パックを追加
    if (packReward > 0) {
      updatedPlayerDataWithReward = {
        ...updatedPlayerDataWithReward,
        unopenedPacks: (updatedPlayerDataWithReward.unopenedPacks || 0) + packReward,
      };
    }

    // 日付進行処理（市場、価格履歴、資産、大会、投資）
    const updatedPlayerData = await processDayAdvancement(updatedPlayerDataWithReward);

    // 投資処理結果を取得してダイアログ用にセット
    const investmentResults = updatedPlayerData._investmentResults;
    if (investmentResults) {
      delete updatedPlayerData._investmentResults;
      const hasEvents = investmentResults.autoOrders.length > 0 ||
        investmentResults.fundLiquidations.length > 0 ||
        investmentResults.fxLiquidations.length > 0 ||
        investmentResults.debtGenerated > 0;
      if (hasEvents) {
        setInvestmentResultData(investmentResults);
      }
    }

    // 差し押さえが必要かチェック
    const seizureNeeded = updatedPlayerData._seizureNeeded;
    if (seizureNeeded) {
      delete updatedPlayerData._seizureNeeded;
    }

    updatePlayerData(updatedPlayerData);
    setBattleReward({
      goldReward,
      packReward,
      isWin,
    });

    // 差し押さえが必要なら差し押さえ画面へ遷移
    if (seizureNeeded) {
      setGameState('seizure');
    }
  }, [playerData, updatePlayerData, processDayAdvancement, adventureBattleContext]);

  // 休んで次の日に進む（対戦報酬なし）
  const restAndAdvanceDay = useCallback(async () => {
    if (!playerData) return;

    // 日付進行処理のみ（報酬なし）
    let updatedPlayerData;
    try { updatedPlayerData = await processDayAdvancement(playerData); }
    catch (error) { alert(`日付更新を確認できませんでした: ${error.message}`); return; }

    // 投資処理結果を取得してダイアログ用にセット
    const investmentResults = updatedPlayerData._investmentResults;
    if (investmentResults) {
      delete updatedPlayerData._investmentResults;
      const hasEvents = investmentResults.autoOrders.length > 0 ||
        investmentResults.fundLiquidations.length > 0 ||
        investmentResults.fxLiquidations.length > 0 ||
        investmentResults.debtGenerated > 0;
      if (hasEvents) {
        setInvestmentResultData(investmentResults);
      }
    }

    // 差し押さえが必要かチェック
    const seizureNeeded = updatedPlayerData._seizureNeeded;
    if (seizureNeeded) {
      delete updatedPlayerData._seizureNeeded;
    }

    updatePlayerData(updatedPlayerData);

    // 差し押さえが必要なら差し押さえ画面へ遷移
    if (seizureNeeded) {
      setGameState('seizure');
    } else {
      // 休息完了ダイアログを表示
      setShowRestDialog(true);
    }
  }, [playerData, updatePlayerData, processDayAdvancement]);

  // ダンジョン（異界の扉）完了時の日付進行処理
  const handleDungeonComplete = useCallback(async () => {
    if (!playerData) return;

    // 日付進行処理（市場、価格履歴、資産、大会など）
    let updatedPlayerData;
    try { updatedPlayerData = await processDayAdvancement(playerData); }
    catch (error) { alert(`日付更新を確認できませんでした: ${error.message}`); return; }

    // 投資処理結果を取得してダイアログ用にセット
    const investmentResults = updatedPlayerData._investmentResults;
    if (investmentResults) {
      delete updatedPlayerData._investmentResults;
      const hasEvents = investmentResults.autoOrders.length > 0 ||
        investmentResults.fundLiquidations.length > 0 ||
        investmentResults.fxLiquidations.length > 0 ||
        investmentResults.debtGenerated > 0;
      if (hasEvents) {
        setInvestmentResultData(investmentResults);
      }
    }

    // 差し押さえが必要かチェック
    const seizureNeeded = updatedPlayerData._seizureNeeded;
    if (seizureNeeded) {
      delete updatedPlayerData._seizureNeeded;
    }

    updatePlayerData(updatedPlayerData);

    // 差し押さえが必要なら差し押さえ画面へ遷移
    if (seizureNeeded) {
      setGameState('seizure');
    }
    // ダンジョン完了の場合は特別なダイアログは表示しない（ResultScreen側で表示済み）
  }, [playerData, updatePlayerData, processDayAdvancement]);

  // パック開封画面へ遷移
  const handleOpenPack = useCallback((cards, packCount = 1, packInfo = null) => {
    setPendingPackCards({ cards, packCount, packInfo });
    setGameState('packOpening');
  }, []);

  // パック開封完了
  const handlePackOpeningClose = useCallback(() => {
    // 報酬パックの場合はゲームオーバー画面に戻る、ショップからの場合はパックタブに戻る
    if (battleReward) {
      setPendingPackCards(null);
      setPackTabInitialState(null);
      setGameState('gameOver');
    } else {
      // パックタブに戻る（開封前のパックタイプと属性を復元）
      const packInfo = pendingPackCards?.packInfo;
      if (packInfo?.type === 'attribute') {
        setPackTabInitialState({ packType: 'attribute', attribute: packInfo.attribute });
      } else if (packInfo?.type === 'royal') {
        setPackTabInitialState({ packType: 'royal', attribute: null });
      } else {
        setPackTabInitialState({ packType: 'standard', attribute: null });
      }
      setPendingPackCards(null);
      setMerchantGuildInitialTab('pack');
      setGameState('merchantGuild');
    }
  }, [battleReward, pendingPackCards]);

  // 大会報酬受け取り
  const handleClaimTournamentReward = useCallback(async (payoutResults) => {
    // 重複呼び出し防止
    if (isClaimingTournamentReward.current) {
      console.log('[Tournament] 報酬請求処理中のため無視');
      return;
    }
    isClaimingTournamentReward.current = true;

    const tournament = pendingTournamentResult || playerData?.tournamentData?.currentTournament;
    if (!tournament) {
      isClaimingTournamentReward.current = false;
      return;
    }

    if (economyAccount()) {
      try {
        await economyRequest('tournament/payouts/claim', { tournament_id: tournament.id });
        setShowTournamentViewer(false); setPendingTournamentResult(null);
        return true;
      } catch (error) { alert(error.message); return false; }
      finally { isClaimingTournamentReward.current = false; }
    }
    const currentBets = playerData?.tournamentData?.currentBets || [];
    const totalPayout = payoutResults?.totalPayout || 0;

    // 履歴エントリ作成
    const historyEntry = createHistoryEntry(tournament, currentBets, payoutResults);

    // 統計更新
    const newTotalStats = updateTotalStats(
      playerData?.tournamentData?.totalStats || {},
      payoutResults
    );

    // サーバーに配当金請求を送信（認証済みの場合）
    if (isBettingServerAvailable() && currentBets.length > 0) {
      claimPayoutsOnServer(tournament.id, {
        winner: tournament.finalWinner,
        second: tournament.finalSecond,
      }).then((result) => {
        if (result.success) {
          console.log('[Tournament] サーバー配当金請求成功:', result);
          // サーバーのゴールド残高で更新
          if (result.new_gold_balance !== undefined) {
            updatePlayerData((prev) => ({
              ...prev,
              gold: result.new_gold_balance,
              tournamentData: {
                ...prev.tournamentData,
                currentTournament: null,
                currentBets: [],
                purchasedInfo: {},
                history: [historyEntry, ...(prev.tournamentData?.history || [])].slice(0, 500),
                totalStats: newTotalStats,
              },
            }));
          }
        } else {
          console.warn('[Tournament] サーバー配当金請求失敗（ローカルで処理済み）:', result.error);
        }
      }).catch((err) => {
        console.error('[Tournament] サーバー配当金請求エラー:', err);
      });
    }

    // ローカルでのプレイヤーデータ更新（報酬付与 + 大会終了）
    // サーバー成功時は後から上書きされる
    updatePlayerData((prev) => ({
      ...prev,
      gold: prev.gold + totalPayout,
      tournamentData: {
        ...prev.tournamentData,
        currentTournament: null, // 大会終了
        currentBets: [], // 賭けクリア
        purchasedInfo: {}, // 購入情報クリア
        history: [historyEntry, ...(prev.tournamentData?.history || [])].slice(0, 500),
        totalStats: newTotalStats,
      },
    }));

    if (totalPayout > 0) {
      console.log(`[Tournament] 払戻: ${totalPayout.toLocaleString()}G`);
    }

    // ダイアログを閉じる
    setShowTournamentViewer(false);
    setPendingTournamentResult(null);

    // 重複防止フラグをリセット
    isClaimingTournamentReward.current = false;

    // 新規大会作成チェック
    const currentDay = playerData?.market?.currentDay || 1;
    const triggerType = checkTournamentTrigger(currentDay, null);
    if (triggerType) {
      createTournament(triggerType, currentDay).then((newTournament) => {
        if (newTournament) {
          updatePlayerData((prev) => ({
            ...prev,
            tournamentData: {
              ...prev.tournamentData,
              currentTournament: newTournament,
            },
          }));
          console.log(`[Tournament] ${newTournament.name} が開催されました`);
        }
      }).catch((err) => {
        console.error('[Tournament] 大会作成エラー:', err);
      });
    }
  }, [pendingTournamentResult, playerData, updatePlayerData]);

  // カード売却（サーバーAPI対応）
  const handleSellCard = useCallback(async (cardId, rarity, quantity, card) => {
    if (!playerData || !cardValueMap) return;

    // サーバーAPI使用フラグ（localStorageで切り替え可能）
    const useServerApi = !!economyAccount() || (() => {
      try {
        return localStorage.getItem('magisp_use_local_pack') !== 'true';
      } catch {
        return true;
      }
    })();

    if (useServerApi) {
      try {
        const { shopSystemServer } = require('./collection/systems/shopSystemServer');
        const result = await shopSystemServer.sellCardAsync(playerData, cardId, rarity, quantity, card);
        if (result.success) {
          updatePlayerData(result.playerData);
        }
      } catch (err) {
        console.error('Server sell error, falling back to local:', err);
        // フォールバック: ローカル処理
        const { shopSystem } = require('./collection');
        const result = shopSystem.sellCard(playerData, cardId, rarity, quantity, card, cardValueMap);
        if (result.success) {
          updatePlayerData(result.playerData);
        }
      }
    } else {
      const { shopSystem } = require('./collection');
      const result = shopSystem.sellCard(playerData, cardId, rarity, quantity, card, cardValueMap);
      if (result.success) {
        updatePlayerData(result.playerData);
      }
    }
  }, [playerData, cardValueMap, updatePlayerData]);

  // デッキ保存
  const handleSaveDeck = useCallback((deck) => {
    if (!playerData) return;

    const userDecks = playerData.userDecks || [];
    const existingIndex = userDecks.findIndex(d => d.id === deck.id);

    let newUserDecks;
    if (existingIndex >= 0) {
      // 既存デッキの更新
      newUserDecks = [...userDecks];
      newUserDecks[existingIndex] = deck;
    } else {
      // 新規デッキの追加
      newUserDecks = [...userDecks, deck];
    }

    const newPlayerData = {
      ...playerData,
      userDecks: newUserDecks,
      updatedAt: Date.now(),
    };

    updatePlayerData(newPlayerData);
    setEditingDeck(null);
    setGameState('deckList');
  }, [playerData, updatePlayerData]);

  // デッキ削除
  const handleDeleteDeck = useCallback((deckId) => {
    if (!playerData) return;

    const userDecks = playerData.userDecks || [];
    const newUserDecks = userDecks.filter(d => d.id !== deckId);

    const newPlayerData = {
      ...playerData,
      userDecks: newUserDecks,
      updatedAt: Date.now(),
    };

    updatePlayerData(newPlayerData);
  }, [playerData, updatePlayerData]);

  // ========================================
  // ゲーム初期化
  const startingBattle = useRef(false);
  const initGame = useCallback(async () => {
    if (startingBattle.current) return;
    startingBattle.current = true;
    try { if (economyAccount()) await startEconomyBattle('normal'); }
    catch (error) { alert(error.message); return; }
    finally { startingBattle.current = false; }
    setAdventureBattleContext(null);
    // 報酬状態をクリア
    setBattleReward(null);

    // デッキ生成ヘルパー: ユーザーデッキかプリビルトか判断
    const createDeckFromSelection = (deckId) => {
      if (deckId.startsWith('user_')) {
        // ユーザーデッキの場合
        const userDeckId = deckId.replace('user_', '');
        const userDeck = playerData?.userDecks?.find(d => d.id === userDeckId);
        if (userDeck) {
          return createDeckFromUserDeck(userDeck, allCards);
        }
        console.warn(`ユーザーデッキが見つかりません: ${userDeckId}`);
        return createDeck(allCards); // フォールバック
      }
      // プリビルトデッキの場合
      return createDeckFromPrebuilt(deckId, allCards);
    };

    // 選択されたデッキからカードを生成
    let deck1, deck2;
    try {
      deck1 = createDeckFromSelection(p1SelectedDeck);
      deck2 = createDeckFromSelection(p2SelectedDeck);
      if (!deck1.length || !deck2.length) throw new Error('デッキに使用できるカードがありません');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'デッキを作成できませんでした');
      return;
    }

    // 先行・後攻をランダムに決定（GameEngineと共有）
    const firstPlayer = Math.random() < 0.5 ? 1 : 2;

    // ========================================
    // GameEngine初期化（ヘッドレス対戦シミュレーション用）
    // 現在はUI状態と並行して管理、将来的に統合予定
    // ========================================
    engineInitGame({
      deck1,
      deck2,
      firstPlayer,
      summoningSickness,
      secondPlayerBonusSP,
    });

    // 既存のuseState初期化（互換性維持）
    setP1Deck(deck1.slice(INITIAL_HAND_SIZE));
    setP1Hand(deck1.slice(0, INITIAL_HAND_SIZE));
    setP2Deck(deck2.slice(INITIAL_HAND_SIZE));
    setP2Hand(deck2.slice(0, INITIAL_HAND_SIZE));

    setP1Life(initialLifePoints);
    setP2Life(initialLifePoints);
    // 後攻プレイヤーは初期SP+1（バランス調整）
    setP1ActiveSP(firstPlayer === 1 ? INITIAL_SP : INITIAL_SP + 1);
    setP2ActiveSP(firstPlayer === 2 ? INITIAL_SP : INITIAL_SP + 1);
    setP1RestedSP(0);
    setP2RestedSP(0);
    setP1NextTurnSPBonus(0);
    setP2NextTurnSPBonus(0);
    setP1MagicBlocked(false);
    setP2MagicBlocked(false);
    setP1Field([null, null, null, null, null]);
    setP2Field([null, null, null, null, null]);
    setP1FieldCard(null);
    setP2FieldCard(null);
    setP1PhaseCard(null);
    setP2PhaseCard(null);
    setP1Graveyard([]);
    setP2Graveyard([]);

    // Phase D-3: turn, currentPlayer, phase, isFirstTurn, winner, logs は engineInitGame() で設定済み
    // 初期ログはengineInitGame内で追加されるため、ここでのset*呼び出しは不要

    // UI選択状態をリセット（Phase 3: Hook utility）
    resetAllSelection();
    setChargeUsedThisTurn(false);

    // 刹那詠唱状態をリセット（Phase 4-1: Hook utility）
    resetAllSetsuna();

    // AIシステムをリセット（Phase 7: Hook utility）
    resetAI();

    // トリガーシステムをクリア
    clearAllTriggers();

    // 常時効果システムをクリア
    continuousEffectEngine.clear();

    // 状態異常システムをクリア
    statusEffectEngine.clear();
    setP1StatusEffects([]);
    setP2StatusEffects([]);

    // 深海領域をリセット
    deepSea.resetAll();

    setGameState('playing');
    addLog('ゲーム開始！先攻プレイヤー1のターン', 'info');
  }, [addLog, allCards, p1SelectedDeck, p2SelectedDeck, playerData, engineInitGame, resetAI, deepSea]);

  // ========================================
  // ドラフトバトル開始
  // ========================================
  const handleDraftBattle = useCallback((playerDeck, opponentDeck, opponentName, onBattleEnd) => {
    // ドラフトバトルコンテキストを保存
    setDraftBattleContext({
      playerDeck,
      opponentDeck,
      opponentName,
      onBattleEnd,
    });

    // 報酬状態をクリア
    setBattleReward(null);

    // デッキからカードインスタンスを生成（uniqueIdを付与）
    const createDeckInstances = (cards) => {
      return shuffle([...cards]).map((card, index) => ({
        ...card,
        uniqueId: `${card.id}_draft_${Date.now()}_${index}`,
        // モンスターの場合は現在値を初期化
        ...(card.type === 'monster' ? {
          currentAttack: card.attack,
          currentHp: card.hp,
          canAttack: false,
          charges: [],
          statusEffects: [],
          usedSkillThisTurn: false,
          linkedTo: null,
          linkedBonus: { atk: 0, hp: 0 },
        } : {}),
      }));
    };

    const deck1 = createDeckInstances(playerDeck);
    const deck2 = createDeckInstances(opponentDeck);

    // 先行・後攻をランダムに決定
    const firstPlayer = Math.random() < 0.5 ? 1 : 2;

    // GameEngine初期化
    engineInitGame({
      deck1,
      deck2,
      firstPlayer,
      summoningSickness,
      secondPlayerBonusSP,
    });

    // useState初期化
    setP1Deck(deck1.slice(INITIAL_HAND_SIZE));
    setP1Hand(deck1.slice(0, INITIAL_HAND_SIZE));
    setP2Deck(deck2.slice(INITIAL_HAND_SIZE));
    setP2Hand(deck2.slice(0, INITIAL_HAND_SIZE));

    setP1Life(initialLifePoints);
    setP2Life(initialLifePoints);
    setP1ActiveSP(firstPlayer === 1 ? INITIAL_SP : INITIAL_SP + 1);
    setP2ActiveSP(firstPlayer === 2 ? INITIAL_SP : INITIAL_SP + 1);
    setP1RestedSP(0);
    setP2RestedSP(0);
    setP1NextTurnSPBonus(0);
    setP2NextTurnSPBonus(0);
    setP1MagicBlocked(false);
    setP2MagicBlocked(false);
    setP1Field([null, null, null, null, null]);
    setP2Field([null, null, null, null, null]);
    setP1FieldCard(null);
    setP2FieldCard(null);
    setP1PhaseCard(null);
    setP2PhaseCard(null);
    setP1Graveyard([]);
    setP2Graveyard([]);

    // UI選択状態をリセット
    resetAllSelection();
    setChargeUsedThisTurn(false);

    // 刹那詠唱状態をリセット
    resetAllSetsuna();

    // AIシステムをリセット
    resetAI();

    // トリガーシステムをクリア
    clearAllTriggers();

    // 常時効果システムをクリア
    continuousEffectEngine.clear();

    // 状態異常システムをクリア
    statusEffectEngine.clear();
    setP1StatusEffects([]);
    setP2StatusEffects([]);

    // AI対戦を有効化（相手はNPC）- 直接state変更でlocalStorage保存を回避
    setP2PlayerTypeState('ai');

    setGameState('playing');
    addLog(`ドラフトバトル開始！ vs ${opponentName}`, 'info');
  }, [addLog, engineInitGame, resetAI]);

  // ========================================
  // アドベンチャーバトル開始
  // ========================================
  const handleAdventureBattle = useCallback(async (config: AdventureBattleConfig & { onBattleEnd?: (npcId: string, result: BattleResult, playerData: any) => { playerData: any; reward: BattleRewardResult } }) => {
    if (startingBattle.current) return;
    startingBattle.current = true;
    try { if (economyAccount()) await startEconomyBattle('adventure', config.npcId); }
    catch (error) { alert(error.message); return; }
    finally { startingBattle.current = false; }
    // アドベンチャーバトルコンテキストを保存（onBattleEndコールバック含む）
    setAdventureBattleContext({
      npcId: config.npcId,
      npcName: config.npcName,
      isFirstBattle: config.isFirstBattle,
      onBattleEnd: config.onBattleEnd,
    });

    // 報酬状態をクリア
    setBattleReward(null);

    // プレイヤーデッキを取得（現在選択中のデッキまたはデフォルト）
    let playerDeckCards = [];
    if (playerData?.userDecks?.length > 0) {
      // 最初の所持デッキを使用
      const userDeck = playerData.userDecks[0];
      playerDeckCards = userDeck.cards.map((cardId) =>
        allCards.find((c) => c.id === cardId)
      ).filter(Boolean);
    }

    // プレイヤーデッキがない場合はスターターデッキを使用
    if (playerDeckCards.length < 40) {
      playerDeckCards = createDeckFromPrebuilt('starter', allCards);
    }

    // NPCデッキをカードIDから展開
    const npcDeckCards = config.npcDeckCardIds.map((cardId) =>
      allCards.find((c) => c.id === cardId)
    ).filter(Boolean);

    // デッキからカードインスタンスを生成（uniqueIdを付与）
    const createDeckInstances = (cards, prefix) => {
      return shuffle([...cards]).map((card, index) => ({
        ...card,
        uniqueId: `${card.id}_${prefix}_${Date.now()}_${index}`,
        // モンスターの場合は現在値を初期化
        ...(card.type === 'monster' ? {
          currentAttack: card.attack,
          currentHp: card.hp,
          canAttack: false,
          charges: [],
          statusEffects: [],
          usedSkillThisTurn: false,
          linkedTo: null,
          linkedBonus: { atk: 0, hp: 0 },
        } : {}),
      }));
    };

    const deck1 = createDeckInstances(playerDeckCards, 'adv_p1');
    const deck2 = createDeckInstances(npcDeckCards, 'adv_p2');

    // 先行・後攻をランダムに決定
    const firstPlayer = Math.random() < 0.5 ? 1 : 2;

    // GameEngine初期化
    engineInitGame({
      deck1,
      deck2,
      firstPlayer,
      summoningSickness,
      secondPlayerBonusSP,
    });

    // useState初期化
    setP1Deck(deck1.slice(INITIAL_HAND_SIZE));
    setP1Hand(deck1.slice(0, INITIAL_HAND_SIZE));
    setP2Deck(deck2.slice(INITIAL_HAND_SIZE));
    setP2Hand(deck2.slice(0, INITIAL_HAND_SIZE));

    setP1Life(initialLifePoints);
    setP2Life(initialLifePoints);
    setP1ActiveSP(firstPlayer === 1 ? INITIAL_SP : INITIAL_SP + 1);
    setP2ActiveSP(firstPlayer === 2 ? INITIAL_SP : INITIAL_SP + 1);
    setP1RestedSP(0);
    setP2RestedSP(0);
    setP1NextTurnSPBonus(0);
    setP2NextTurnSPBonus(0);
    setP1MagicBlocked(false);
    setP2MagicBlocked(false);
    setP1Field([null, null, null, null, null]);
    setP2Field([null, null, null, null, null]);
    setP1FieldCard(null);
    setP2FieldCard(null);
    setP1PhaseCard(null);
    setP2PhaseCard(null);
    setP1Graveyard([]);
    setP2Graveyard([]);

    // UI選択状態をリセット
    resetAllSelection();
    setChargeUsedThisTurn(false);

    // 刹那詠唱状態をリセット
    resetAllSetsuna();

    // AIシステムをリセット
    resetAI();

    // トリガーシステムをクリア
    clearAllTriggers();

    // 常時効果システムをクリア
    continuousEffectEngine.clear();

    // 状態異常システムをクリア
    statusEffectEngine.clear();
    setP1StatusEffects([]);
    setP2StatusEffects([]);

    // AI対戦を有効化（相手はNPC）- 直接state変更でlocalStorage保存を回避
    // AI難易度を設定
    setP2PlayerTypeState('ai');
    setP2AIDifficulty(config.aiDifficulty || 'hard');

    setGameState('playing');
    addLog(`アドベンチャーバトル開始！ vs ${config.npcName}`, 'info');
  }, [addLog, allCards, playerData, engineInitGame, resetAI]);

  // フェイズ処理は usePhaseProcessor Hook に移動（Phase 6-1）
  // processPhase は上部の usePhaseProcessor 呼び出しから取得

  // チャージ処理は useSummon Hook に移動（Phase 6-2）
  // chargeCard, chargeSP, chargePhaseCard は上部の useSummon 呼び出しから取得

  // 技発動処理は useSkillExecution Hook に移動（Phase 6-4）
  // executeSkill は上部の useSkillExecution 呼び出しから取得

  // 【壮麗】→ Phase 4-2: useSourei Hook に移動

  // トリガーコンテキスト生成関数（CenterZone用）
  const createTriggerContext = useCallback((monster, slotIndex) => ({
    currentPlayer,
    card: monster,
    slotIndex,
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1Life,
    p2Life,
    addLog,
    setPendingHandSelection,
    setPendingGraveyardSelection,
    setShowGraveyardViewer,
    setPendingDeckReview,
  }), [currentPlayer, p1Field, p2Field, p1Hand, p2Hand, p1Deck, p2Deck,
       p1Graveyard, p2Graveyard, p1Life, p2Life, addLog, setPendingHandSelection,
       setPendingGraveyardSelection, setShowGraveyardViewer, setPendingDeckReview,
       setP1Life, setP2Life, setP1Field, setP2Field, setP1Hand, setP2Hand,
       setP1Deck, setP2Deck, setP1Graveyard, setP2Graveyard]);

  // 【犠現】→ Phase 4-2: useGigen Hook に移動
  // 【未来予知】→ Phase 4-2: useMiraiYochi Hook に移動

  // カード召喚は useSummon Hook に移動（Phase 6-2）
  // summonCard は上部の useSummon 呼び出しから取得

  // 攻撃処理は useCombat Hook に移動（Phase 6-3）
  // executeAttack, attack は上部の useCombat 呼び出しから取得

  // 勝敗判定
  useEffect(() => {
    if (gameState !== 'playing') return;

    let battleWinner = null;
    if (p1Life <= 0) {
      battleWinner = 2;
    } else if (p2Life <= 0) {
      battleWinner = 1;
    }

    if (battleWinner) {
      dispatch(gameActions.setWinner(battleWinner));

      // ドラフトバトルの場合は結果を返してドラフト画面に戻る
      if (draftBattleContext) {
        const didWin = battleWinner === 1;
        addLog(didWin ? 'ドラフトバトル勝利！' : 'ドラフトバトル敗北...', didWin ? 'heal' : 'damage');

        // セッションに直接バトル結果を保存（コールバックはDraftAppアンマウント後に無効になるため）
        try {
          // loadDraftSession() returns { meta, summary, state } structure
          const sessionData = loadDraftSession();
          const sessionState = sessionData?.state;
          console.log('[magic-spirit] Draft session loaded:', {
            hasSessionData: !!sessionData,
            hasState: !!sessionState,
            hasTournament: !!sessionState?.tournament,
            currentMatchId: sessionState?.tournament?.currentMatchId,
            matchesCount: sessionState?.tournament?.matches?.length,
          });
          if (sessionData && sessionState?.tournament) {
            sessionState.tournament.pendingBattleResult = didWin ? 'win' : 'lose';
            // Re-save the session with updated tournament state
            saveDraftSession(sessionState);
            console.log('[magic-spirit] Battle result saved to session:', didWin ? 'win' : 'lose');
          } else {
            console.warn('[magic-spirit] Cannot save battle result - no tournament state in session');
          }
        } catch (e) {
          console.error('[magic-spirit] Failed to save draft battle result:', e);
        }

        // ドラフト画面に戻る
        setDraftBattleContext(null);
        setGameState('draft');
        return;
      }

      // アドベンチャーバトルの場合は結果を処理してアドベンチャー画面に戻る
      if (adventureBattleContext) {
        if (economyAccount()) { setGameState('gameOver'); return; }
        const didWin = battleWinner === 1;
        const result: BattleResult = didWin ? 'win' : 'lose';
        addLog(didWin ? 'アドベンチャーバトル勝利！' : 'アドベンチャーバトル敗北...', didWin ? 'heal' : 'damage');

        // useAdventure の handleBattleEnd を呼び出し
        // （進行データ更新、報酬付与、サーバー同期は useAdventure 側で処理）
        if (adventureBattleContext.onBattleEnd && playerData) {
          const { playerData: updatedPlayerData, reward } = adventureBattleContext.onBattleEnd(
            adventureBattleContext.npcId,
            result,
            playerData
          );

          // プレイヤーデータを更新
          if (didWin) {
            updatePlayerData(updatedPlayerData);

            // 全クリアボーナスのチェック（ログ表示用）
            if (reward.title === 'マジックスピリット・マスター') {
              addLog('全NPCクリア達成！特別ボーナスを獲得！', 'heal');
            }
          }

          console.log('[magic-spirit] Adventure battle result processed:', {
            npcId: adventureBattleContext.npcId,
            result,
            reward,
          });
        }

        // アドベンチャー画面に戻る
        setAdventureBattleContext(null);
        setGameState('adventure');
        return;
      }

      // 通常のゲーム終了
      setGameState('gameOver');
    }
  }, [p1Life, p2Life, gameState, dispatch, draftBattleContext, adventureBattleContext, playerData, updatePlayerData, addLog]);

  // フェイズ自動進行
  useEffect(() => {
    if (gameState !== 'playing') return;
    if (phase === 0 || phase === 1) {
      const timer = setTimeout(() => processPhase(phase), getScaledDelay(500));
      return () => clearTimeout(timer);
    }
  }, [phase, gameState, processPhase]);

  // バトルフェイズ開始時のAI攻撃済みモンスターリセットは useAIController Hook に移動（Phase 7）

  // 刹那詠唱効果完了後のアクション実行
  // pendingMonsterTarget/pendingHandSelectionがクリアされたらpendingSetsunaActionを実行
  useEffect(() => {
    if (!pendingSetsunaAction) return;
    // pending系の処理がまだある場合は待機
    if (pendingMonsterTarget || pendingHandSelection || pendingGraveyardSelection || pendingDeckReview) {
      return;
    }

    // 少し遅延してアクションを実行（状態更新の反映を待つ）
    const timeoutId = setTimeout(() => {
      if (pendingSetsunaAction.type === 'attack') {
        executeAttack(pendingSetsunaAction.attackerIndex, pendingSetsunaAction.targetIndex);
      } else if (pendingSetsunaAction.type === 'battleStart') {
        dispatch(gameActions.setPhase(3));
        setSelectedHandCard(null);
        // バトルフェイズ開始時トリガーを発火
        processPhase(3);
      }
      setPendingSetsunaAction(null);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pendingSetsunaAction, pendingMonsterTarget, pendingHandSelection, pendingGraveyardSelection, pendingDeckReview, executeAttack, dispatch, processPhase]);

  // AIターン実行は useAIController Hook に移動（Phase 7）

  // ハンドカードクリック
  const handleHandCardClick = (card) => {
    if (phase !== 2) return;
    // 現在のプレイヤーの手札かどうかチェック
    const currentHand = currentPlayer === 1 ? p1Hand : p2Hand;
    if (!currentHand.find(c => c.uniqueId === card.uniqueId)) return;

    // 手札選択待ち状態の場合：pendingSelectedCardを使用（通常のアクションを無効化）
    if (pendingHandSelection) {
      // フィルターがある場合はチェック
      if (pendingHandSelection.filter && !pendingHandSelection.filter(card)) {
        addLog('そのカードは選択できません', 'damage');
        return;
      }
      // 選択状態を切り替え（同じカードをクリックで選択解除）
      setPendingSelectedCard(pendingSelectedCard?.uniqueId === card.uniqueId ? null : card);
      return;
    }

    setSelectedHandCard(selectedHandCard?.uniqueId === card.uniqueId ? null : card);
    setSelectedFieldMonster(null);
    setAttackingMonster(null);
  };

  // フィールドスロットクリック
  const handleFieldSlotClick = (slotIndex, playerNum) => {
    // 手札選択待ち中は操作不可
    if (pendingHandSelection) return;

    // モンスターターゲット選択モード
    if (pendingMonsterTarget) {
      // selectionOwnerを使ってターゲット判定（刹那詠唱で相手ターン中でも正しく判定）
      const owner = pendingMonsterTarget.selectionOwner ?? currentPlayer;
      const isMyField = playerNum === owner;
      const targetField = pendingMonsterTarget.targetPlayer === 'opponent'
        ? (owner === 1 ? p2Field : p1Field)
        : (owner === 1 ? p1Field : p2Field);
      const isValidField = pendingMonsterTarget.targetPlayer === 'opponent' ? !isMyField : isMyField;

      if (isValidField && targetField[slotIndex]) {
        setPendingSelectedMonsterIndex(slotIndex);
      }
      return;
    }

    // 現在のプレイヤーの場か相手の場かを判定
    const isMyField = playerNum === currentPlayer;

    if (phase === 2 && isMyField) {
      const field = currentPlayer === 1 ? p1Field : p2Field;
      const monster = field[slotIndex];

      if (selectedHandCard) {
        // チャージモード（モンスターが存在する場合）
        if (monster && (selectedHandCard.type === 'monster' || selectedHandCard.type === 'magic' || selectedHandCard.type === 'field' || selectedHandCard.type === 'phasecard')) {
          if (chargeCard(selectedHandCard, slotIndex)) {
            setSelectedHandCard(null);
          }
        }
        // 召喚モード（空きスロットの場合）
        else if (!monster && selectedHandCard.type === 'monster') {
          if (summonCard(selectedHandCard, slotIndex)) {
            setSelectedHandCard(null);
          }
        }
      } else {
        // モンスター選択（技発動用）
        if (monster) {
          setSelectedFieldMonster(selectedFieldMonster === slotIndex ? null : slotIndex);
          setSelectedHandCard(null);
        }
      }
    } else if (phase === 3 && isMyField) {
      // 攻撃者選択
      const field = currentPlayer === 1 ? p1Field : p2Field;
      const monster = field[slotIndex];
      if (monster && monster.canAttack) {
        setAttackingMonster(slotIndex);
        setSelectedFieldMonster(slotIndex);
      }
    } else if (phase === 3 && !isMyField && attackingMonster !== null) {
      // 攻撃対象選択
      const currentField = currentPlayer === 1 ? p1Field : p2Field;
      const opponentField = currentPlayer === 1 ? p2Field : p1Field;
      const attacker = currentField[attackingMonster];
      const hasTarget = opponentField.some(m => m !== null);

      // 相手フィールドが空の場合は空スロットクリックでダイレクトアタック
      if (!hasTarget) {
        attack(attackingMonster, -1);
      } else if (opponentField[slotIndex]) {
        // モンスターがいるスロットをクリックした場合のみ攻撃
        attack(attackingMonster, slotIndex);
      } else {
        // 相手にモンスターがいる状態で空スロットをクリックした場合
        // 飛行システムでダイレクトアタック可能か確認
        const flightDirectStatus = attacker ? getDirectAttackStatus(attacker, opponentField) : { canDirect: false };
        if (flightDirectStatus.canDirect || (attacker && attacker.canDirectAttackThisTurn) || canIgnoreBlockWithIatsuko()) {
          attack(attackingMonster, -1);
        }
        // それ以外は何もしない
      }
    }
  };

  // ダイレクトアタック
  const handleDirectAttack = () => {
    if (attackingMonster === null) return;
    const currentField = currentPlayer === 1 ? p1Field : p2Field;
    const opponentField = currentPlayer === 1 ? p2Field : p1Field;
    const attacker = currentField[attackingMonster];
    const hasTarget = opponentField.some(m => m !== null);

    // 飛行システムによるダイレクトアタック可否を判定
    const flightDirectStatus = attacker ? getDirectAttackStatus(attacker, opponentField) : { canDirect: false };

    // 相手にモンスターがいない場合、または特殊条件でダイレクトアタック可能な場合
    if (!hasTarget ||
        (attacker && attacker.canDirectAttackThisTurn) ||
        flightDirectStatus.canDirect ||
        canIgnoreBlockWithIatsuko()) {
      attack(attackingMonster, -1);
    } else {
      addLog('相手の場にモンスターがいます。対象を選択してください。', 'info');
    }
  };

  // 潜航処理
  const handleSubmerge = useCallback((card: HandCard) => {
    // 手札から削除するコールバック
    const removeFromHand = (removedCard: HandCard) => {
      if (currentPlayer === 1) {
        setP1Hand(prev => prev.filter(c => c.uniqueId !== removedCard.uniqueId));
      } else {
        setP2Hand(prev => prev.filter(c => c.uniqueId !== removedCard.uniqueId));
      }
    };

    // カードのトリガーをチェック（初期深度+2のヴェルゼファール等）
    const cardTriggers = parseCardTriggers(card);
    const submergeTriggersWithDepthBonus = cardTriggers.filter(
      (t) => t.type === TRIGGER_TYPES.ON_SUBMERGE
    );

    // ヴェルゼファール (C0000527) は初期深度2
    let initialDepth = 1;
    if (card.id === 'C0000527') {
      initialDepth = 2;
    }

    // 潜航実行
    const success = deepSea.handleSubmerge(
      currentPlayer,
      card,
      turn,
      initialDepth,
      removeFromHand,
      addLog
    );

    if (success) {
      // ON_SUBMERGEトリガーを発火（カード固有の潜航時効果）
      if (submergeTriggersWithDepthBonus.length > 0) {
        const submergeContext = {
          currentPlayer,
          card,
          slotIndex: undefined,
          addLog,
          p1Field,
          p2Field,
          setP1Field,
          setP2Field,
          p1Graveyard,
          p2Graveyard,
          setP1Graveyard,
          setP2Graveyard,
          p1Hand,
          p2Hand,
          setP1Hand,
          setP2Hand,
          p1Deck,
          p2Deck,
          setP1Deck,
          setP2Deck,
          p1Life,
          p2Life,
          setP1Life,
          setP2Life,
          p1ActiveSP,
          p2ActiveSP,
          setP1ActiveSP,
          setP2ActiveSP,
          p1RestedSP,
          p2RestedSP,
          setP1RestedSP,
          setP2RestedSP,
          p1DeepSeaZone: deepSea.p1DeepSeaZone,
          p2DeepSeaZone: deepSea.p2DeepSeaZone,
          setP1DeepSeaZone: deepSea.setP1DeepSeaZone,
          setP2DeepSeaZone: deepSea.setP2DeepSeaZone,
          setPendingMonsterTarget,
          setPendingHandSelection,
        };

        // 各ON_SUBMERGEトリガーを実行
        submergeTriggersWithDepthBonus.forEach((trigger) => {
          if (trigger.activationType === ACTIVATION_TYPES.AUTOMATIC && trigger.effect) {
            try {
              trigger.effect(submergeContext as any);
            } catch (error) {
              console.error(`潜航時トリガー実行エラー: ${card.name}`, error);
            }
          }
        });
      }

      // 選択状態をリセット
      setSelectedHandCard(null);
    }
  }, [currentPlayer, turn, deepSea, addLog, setP1Hand, setP2Hand, setSelectedHandCard,
      p1Field, p2Field, setP1Field, setP2Field, p1Graveyard, p2Graveyard,
      setP1Graveyard, setP2Graveyard, p1Hand, p2Hand, p1Deck, p2Deck,
      setP1Deck, setP2Deck, p1Life, p2Life, setP1Life, setP2Life,
      p1ActiveSP, p2ActiveSP, setP1ActiveSP, setP2ActiveSP,
      p1RestedSP, p2RestedSP, setP1RestedSP, setP2RestedSP,
      setPendingMonsterTarget, setPendingHandSelection]);

  // フィールドカードゾーンクリック
  const handleFieldCardZoneClick = () => {
    if (phase !== 2) return;

    const currentFieldCard = currentPlayer === 1 ? p1FieldCard : p2FieldCard;

    // クリックして情報表示
    if (currentFieldCard && !selectedHandCard) {
      setSelectedFieldCardInfo({ card: currentFieldCard, type: 'field', player: currentPlayer });
      return;
    }

    // 手札からフィールドカードを設置
    if (selectedHandCard) {
      // 既にフィールドカードが設置されている場合
      if (currentFieldCard) {
        addLog('既にフィールドカードが設置されています', 'damage');
        return;
      }

      // フィールドカードを設置
      if (selectedHandCard.type === 'field') {
        if (summonCard(selectedHandCard, 0)) {
          setSelectedHandCard(null);
        }
      } else {
        addLog('フィールドゾーンに設置できるのはフィールドカードのみです', 'damage');
      }
    }
  };

  // フェイズカードゾーンクリック
  const handlePhaseCardZoneClick = () => {
    if (phase !== 2) return;

    const currentPhaseCard = currentPlayer === 1 ? p1PhaseCard : p2PhaseCard;

    // クリックして情報表示
    if (currentPhaseCard && !selectedHandCard) {
      setSelectedFieldCardInfo({ card: currentPhaseCard, type: 'phasecard', player: currentPlayer });
      return;
    }

    // 手札からカードを選択している場合
    if (selectedHandCard) {
      // フェイズカードが設置されている場合 → チャージモード
      if (currentPhaseCard) {
        // 属性カード（モンスター、魔法、フィールド、フェイズカード）をチャージ
        if (selectedHandCard.type === 'monster' || selectedHandCard.type === 'magic' || selectedHandCard.type === 'field' || selectedHandCard.type === 'phasecard') {
          if (chargePhaseCard(selectedHandCard)) {
            setSelectedHandCard(null);
          }
        } else {
          addLog('フェイズカードにチャージできるのは属性カード（モンスター、魔法、フィールド、フェイズ）のみです', 'damage');
        }
        return;
      }

      // フェイズカードを設置
      if (selectedHandCard.type === 'phasecard') {
        if (summonCard(selectedHandCard, 0)) {
          setSelectedHandCard(null);
        }
      } else {
        addLog('フェイズカードゾーンに設置できるのはフェイズカードのみです', 'damage');
      }
    }
  };

  // nextPhase は useAIController より前に定義済み（line ~1035）

  // バトルフェイズへ進行（チェーン確認完了後に呼ばれる）
  const proceedToBattlePhase = useCallback(() => {
    // Phase D-1: dispatchのみで状態更新
    dispatch(gameActions.setPhase(3));
    setSelectedHandCard(null);
    // バトルフェイズ開始時トリガーを発火
    processPhase(3);
  }, [dispatch, processPhase]);

  // 魔法カード発動
  const useMagicCard = () => {
    if (selectedHandCard && selectedHandCard.type === 'magic') {
      summonCard(selectedHandCard, 0);
      setSelectedHandCard(null);
    }
  };

  // ========================================
  // 刹那詠唱システム → Phase 4-1: useSetsunaMagic Hook に移動
  // ========================================

  // 墓地発動可能なカードを取得
  const getActivatableGraveyardCards = () => {
    const graveyard = currentPlayer === 1 ? p1Graveyard : p2Graveyard;
    const field = currentPlayer === 1 ? p1Field : p2Field;
    const hasEmptySlot = field.some((slot) => slot === null);

    return graveyard.filter((card) => {
      const triggers = parseCardTriggers(card);
      return triggers.some((trigger) => {
        if (trigger.type !== TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD) {
          return false;
        }
        // コストチェック（深海のクラーケンはSP4必要）
        if (trigger.costCheck) {
          const context = { currentPlayer, p1ActiveSP, p2ActiveSP };
          if (!trigger.costCheck(context)) {
            return false;
          }
        }
        // フィールド空きチェック（蘇生カードの場合）
        if (card.type === 'monster' && !hasEmptySlot) {
          return false;
        }
        return true;
      });
    });
  };

  // 墓地発動を実行
  const activateGraveyardCard = (card) => {
    const triggers = parseCardTriggers(card);
    const graveyardTrigger = triggers.find(
      (t) => t.type === TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD
    );

    if (!graveyardTrigger) {
      addLog('墓地発動できません', 'damage');
      return;
    }

    const triggerContext = {
      currentPlayer,
      p1ActiveSP, p2ActiveSP,
      setP1ActiveSP, setP2ActiveSP,
      p1RestedSP, p2RestedSP,
      setP1RestedSP, setP2RestedSP,
      p1Field, p2Field,
      setP1Field, setP2Field,
      p1Graveyard, p2Graveyard,
      setP1Graveyard, setP2Graveyard,
      p1Hand, p2Hand,
      setP1Hand, setP2Hand,
      p1Deck, p2Deck,
      setP1Deck, setP2Deck,
      p1Life, p2Life,
      setP1Life, setP2Life,
      card,
      addLog,
      setPendingHandSelection,
      setPendingGraveyardSelection,
      setShowGraveyardViewer,
      setPendingDeckReview,
    };

    try {
      if (typeof graveyardTrigger.effect === 'function') {
        graveyardTrigger.effect(triggerContext);
      }
    } catch (error) {
      console.error('墓地発動エラー:', error);
      addLog(`${card.name}の墓地発動に失敗しました`, 'damage');
    }
  };

  // ========================================
  // レンダリング
  // ========================================

  // 墓地発動可能カードを事前計算（パフォーマンス最適化）
  const activatableGraveyardCards = getActivatableGraveyardCards();

  // 商人ギルドへの遷移（タブ指定付き）
  const goToMerchantGuild = (tab = 'merchant') => {
    setMerchantGuildInitialTab(tab);
    setGameState('merchantGuild');
  };

  // Hook呼び出し後に表示を切り替え、保存失敗中の追加操作を防ぐ。
  if (persistenceIssue) {
    return <SaveRecoveryScreen
      message={persistenceIssue.message}
      hasCurrentData={Boolean(persistenceIssue.data)}
      onImport={async (file) => {
        if (!window.confirm('現在の保存内容をバックアップして、選択したJSONに置き換えます。未保存の進行は先に退避してください。復元しますか？')) return;
        try {
          if (!storage.import(await file.text())) {
            window.alert('復元できませんでした。ファイルの形式・バージョン・保存領域を確認してください。');
            return;
          }
          window.location.reload();
        } catch { window.alert('ファイルを読み込めませんでした。保存内容は置き換えていません。'); }
      }}
      onExport={(kind) => {
        try {
          const raw = kind === 'current'
            ? JSON.stringify({ version: STORAGE.VERSION, data: persistenceIssue.data, updatedAt: Date.now() })
            : kind === 'backup' ? storage.exportBackup() : storage.export();
          if (raw === null) { window.alert('退避するデータがありません。'); return; }
          const url = URL.createObjectURL(new Blob([raw], { type: 'application/json' }));
          const link = document.createElement('a');
          link.href = url;
          link.download = `magisp-${kind}-${Date.now()}.json`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch { window.alert('データの退避に失敗しました。ブラウザーの保存領域の設定を確認してください。'); }
      }}
    />;
  }

  if (isLoadingCards) return <p role="status">カードとセーブデータを読み込んでいます…</p>;

  // タイトル画面（Phase 1: 分離済み）
  if (gameState === 'title') {
    return (
      <>
        <TitleScreen
          isMobile={isMobile}
          onStartGame={() => setGameState('menu')}
          onHelpClick={() => setShowHelp(true)}
        />
        <HelpModal
          isOpen={showHelp}
          onClose={() => setShowHelp(false)}
        />
      </>
    );
  }

  // メニュー画面
  if (economyAccount() && (playerData?.economyUserId !== economyAccount() || !Number.isSafeInteger(playerData?.economyVersion))) {
    return <div style={styles.container}><div role="status" style={{ padding: 32 }}>
      <p>オンラインの所持データを読み込んでいます。</p>
      <button onClick={() => authState.refreshServerPlayerData()}>再読み込み</button>
    </div></div>;
  }

  if (gameState === 'menu') {
    return (
      <MenuScreen
        isMobile={isMobile}
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        allCards={allCards}
        setGameState={setGameState}
        setMerchantGuildInitialTab={setMerchantGuildInitialTab}
        restAndAdvanceDay={restAndAdvanceDay}
        showRestDialog={showRestDialog}
        setShowRestDialog={setShowRestDialog}
        investmentResultData={investmentResultData}
        setInvestmentResultData={setInvestmentResultData}
        authState={authState}
      />
    );
  }


  // 異界の扉（ツインキーシステム）
  if (gameState === 'twinKey') {
    return (
      <TwinKeyApp
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        allCards={allCards}
        onBack={() => setGameState('menu')}
        onDungeonComplete={handleDungeonComplete}
      />
    );
  }

  // ブースタードラフト
  if (gameState === 'draft') {
    return (
      <DraftApp
        allCards={allCards}
        cardValueMap={cardValueMap}
        onExit={() => setGameState('menu')}
        onStartBattle={handleDraftBattle}
      />
    );
  }

  // アドベンチャーモード
  if (gameState === 'adventure') {
    return (
      <AdventureScreen
        onBack={() => setGameState('menu')}
        onStartBattle={handleAdventureBattle}
        isMobile={isMobile}
      />
    );
  }

  // デュエル準備画面
  if (gameState === 'duelSetup') {
    return (
      <div style={{...styles.container, overflow: 'auto'}}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
          minHeight: '100vh',
          gap: isMobile ? '16px' : '20px',
          padding: isMobile ? '16px 12px' : '24px 0',
          boxSizing: 'border-box',
        }}>
          <h2 style={{
            fontSize: isMobile ? '20px' : '28px',
            fontWeight: 'bold',
            color: '#e0e0e0',
            marginBottom: 0,
          }}>
            ⚔️ デュエル準備
          </h2>

          {/* デッキ選択UI */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '32px',
            width: isMobile ? '100%' : 'auto',
            maxWidth: isMobile ? '320px' : 'none',
          }}>
            {/* プレイヤー1 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              padding: '16px',
              background: 'rgba(107,158,255,0.1)',
              borderRadius: '12px',
              border: '2px solid rgba(107,158,255,0.4)',
              width: isMobile ? '100%' : '200px',
            }}>
              <label style={{ color: '#6b9eff', fontSize: '14px', fontWeight: 'bold' }}>
                プレイヤー1
              </label>
              <select
                value={p1SelectedDeck}
                onChange={(e) => setP1SelectedDeck(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '2px solid #6b9eff',
                  background: '#1a1a2e',
                  color: '#fff',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <optgroup label="プリセットデッキ">
                  {getDeckOptions().map(deck => (
                    <option key={deck.id} value={deck.id}>{deck.name}</option>
                  ))}
                </optgroup>
                {playerData?.userDecks?.length > 0 && (
                  <optgroup label="所持デッキ">
                    {playerData.userDecks.map(deck => (
                      <option key={deck.id} value={`user_${deck.id}`}>📦 {deck.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <select
                value={p1PlayerType}
                onChange={(e) => setP1PlayerType(e.target.value)}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  borderRadius: '6px',
                  border: '1px solid #6b9eff',
                  background: '#1a1a2e',
                  color: '#fff',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <option value="human">👤 人間</option>
                <option value="ai">🤖 AI</option>
              </select>
              {p1PlayerType === 'ai' && (
                <select
                  value={p1AIDifficulty}
                  onChange={(e) => setP1AIDifficulty(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(107,158,255,0.5)',
                    background: '#1a1a2e',
                    color: '#fff',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  <option value="easy">かんたん</option>
                  <option value="normal">ふつう</option>
                  <option value="hard">むずかしい</option>
                  <option value="evolved">🧬 進化型AI</option>
                  <option value="evolvedSim">🔬 進化型AI(実験)</option>
                </select>
              )}
            </div>

            {/* プレイヤー2 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              padding: '16px',
              background: 'rgba(255,107,107,0.1)',
              borderRadius: '12px',
              border: '2px solid rgba(255,107,107,0.4)',
              width: isMobile ? '100%' : '200px',
            }}>
              <label style={{ color: '#ff6b6b', fontSize: '14px', fontWeight: 'bold' }}>
                プレイヤー2
              </label>
              <select
                value={p2SelectedDeck}
                onChange={(e) => setP2SelectedDeck(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '2px solid #ff6b6b',
                  background: '#1a1a2e',
                  color: '#fff',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <optgroup label="プリセットデッキ">
                  {getDeckOptions().map(deck => (
                    <option key={deck.id} value={deck.id}>{deck.name}</option>
                  ))}
                </optgroup>
                {playerData?.userDecks?.length > 0 && (
                  <optgroup label="所持デッキ">
                    {playerData.userDecks.map(deck => (
                      <option key={deck.id} value={`user_${deck.id}`}>📦 {deck.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <select
                value={p2PlayerType}
                onChange={(e) => setP2PlayerType(e.target.value)}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  borderRadius: '6px',
                  border: '1px solid #ff6b6b',
                  background: '#1a1a2e',
                  color: '#fff',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <option value="human">👤 人間</option>
                <option value="ai">🤖 AI</option>
              </select>
              {p2PlayerType === 'ai' && (
                <select
                  value={p2AIDifficulty}
                  onChange={(e) => setP2AIDifficulty(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,107,107,0.5)',
                    background: '#1a1a2e',
                    color: '#fff',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  <option value="easy">かんたん</option>
                  <option value="normal">ふつう</option>
                  <option value="hard">むずかしい</option>
                  <option value="evolved">🧬 進化型AI</option>
                  <option value="evolvedSim">🔬 進化型AI(実験)</option>
                </select>
              )}
            </div>
          </div>

          {/* AI思考速度設定 */}
          {(p1PlayerType === 'ai' || p2PlayerType === 'ai') && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 20px',
              background: 'rgba(255,215,0,0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255,215,0,0.3)',
            }}>
              <label style={{ color: '#ffd700', fontSize: '14px', fontWeight: 'bold' }}>
                ⚡ AI思考速度
              </label>
              <select
                value={aiThinkingSpeed}
                onChange={(e) => handleAiThinkingSpeedChange(e.target.value)}
                style={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  border: '2px solid #ffd700',
                  background: '#1a1a2e',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {Object.entries(AI_SPEED_SETTINGS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          )}

          {/* 詳細設定トグルボタン */}
          <button
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: showAdvancedSettings ? 'rgba(100,149,237,0.2)' : 'rgba(100,100,120,0.2)',
              border: `1px solid ${showAdvancedSettings ? 'rgba(100,149,237,0.5)' : 'rgba(100,100,120,0.3)'}`,
              borderRadius: '8px',
              color: showAdvancedSettings ? '#6495ed' : '#888',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <span style={{ fontSize: '18px' }}>⚙️</span>
            詳細設定
            <span style={{
              transform: showAdvancedSettings ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}>▼</span>
          </button>

          {/* 詳細設定パネル */}
          {showAdvancedSettings && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '16px',
              background: 'rgba(30,30,50,0.5)',
              borderRadius: '12px',
              border: '1px solid rgba(100,149,237,0.3)',
            }}>
              {/* 召喚酔い設定 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 20px',
                background: 'rgba(100,149,237,0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(100,149,237,0.3)',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#6495ed',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={summoningSickness}
                    onChange={(e) => setSummoningSickness(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#6495ed',
                    }}
                  />
                  召喚酔い（召喚ターンは攻撃不可）
                </label>
              </div>

              {/* 後攻ボーナスSP設定 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 20px',
                background: 'rgba(255,215,0,0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(255,215,0,0.3)',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#ffd700',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={secondPlayerBonusSP}
                    onChange={(e) => setSecondPlayerBonusSP(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#ffd700',
                    }}
                  />
                  後攻ボーナス（後攻に+1SP付与）
                </label>
              </div>

              {/* 貫通ダメージ設定 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 20px',
                background: 'rgba(255,69,0,0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(255,69,0,0.3)',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#ff6347',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={piercingDamage}
                    onChange={(e) => setPiercingDamage(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#ff6347',
                    }}
                  />
                  貫通ダメージ（余剰ダメージの50%が相手ライフへ）
                </label>
              </div>

              {/* 初期LP設定 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 20px',
                background: 'rgba(50,205,50,0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(50,205,50,0.3)',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#32cd32',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}>
                  初期LP:
                  <input
                    type="number"
                    value={initialLifePoints}
                    onChange={(e) => setInitialLifePoints(e.target.value)}
                    min="1000"
                    max="99999"
                    step="1000"
                    style={{
                      width: '80px',
                      padding: '4px 8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#32cd32',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(50,205,50,0.5)',
                      borderRadius: '4px',
                      textAlign: 'center',
                    }}
                  />
                </label>
                <span style={{ fontSize: '12px', color: '#888' }}>（1000〜99999）</span>
              </div>

              {/* 孤軍奮闘設定 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 20px',
                background: 'rgba(50,205,50,0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(50,205,50,0.3)',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#32cd32',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={loneWarriorRule}
                    onChange={(e) => setLoneWarriorRule(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#32cd32',
                    }}
                  />
                  孤軍奮闘（少数側にATK・被ダメ軽減ボーナス）
                </label>
              </div>
            </div>
          )}

          {/* ボタン */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '10px' : '16px',
            width: isMobile ? '100%' : 'auto',
            maxWidth: isMobile ? '320px' : 'none',
          }}>
            <button
              onClick={() => setGameState('menu')}
              style={{
                ...styles.actionButton,
                background: 'rgba(100,100,120,0.5)',
                fontSize: isMobile ? '14px' : '16px',
                padding: isMobile ? '12px 24px' : '12px 32px',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              ← 戻る
            </button>
            <button
              onClick={initGame}
              style={{
                ...styles.actionButton,
                fontSize: isMobile ? '16px' : '18px',
                padding: isMobile ? '14px 24px' : '14px 48px',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              ⚔️ 開始！
            </button>
          </div>
        </div>
      </div>
    );
  }

  // コレクション画面
  if (gameState === 'collection') {
    return (
      <CollectionScreen
        playerData={playerData}
        allCards={allCards}
        cardValueMap={cardValueMap}
        onBack={() => setGameState('menu')}
        onSellCard={handleSellCard}
        onSettingsChange={(newSettings) => {
          updatePlayerData({
            ...playerData,
            settings: newSettings,
          });
        }}
      />
    );
  }

  // カード図鑑画面
  if (gameState === 'cardDex') {
    return (
      <CardDexScreen
        onBack={() => setGameState('menu')}
        allCards={allCards}
        playerData={playerData}
        updatePlayerData={updatePlayerData}
      />
    );
  }

  // AI分析画面
  if (gameState === 'aiAnalysis') {
    return (
      <AIAnalysisScreen
        allCards={allCards}
        playerData={playerData}
        onClose={() => setGameState('menu')}
      />
    );
  }

  // 商人ギルド画面
  if (gameState === 'merchantGuild') {
    return (
      <>
        <MerchantGuild
          playerData={playerData}
          allCards={allCards}
          cardValueMap={cardValueMap}
          initialTab={merchantGuildInitialTab}
          packTabInitialState={packTabInitialState}
          onBack={() => setGameState('menu')}
          onOpenPack={handleOpenPack}
          onGoToCollection={() => setGameState('collection')}
          onOpenMarketAnalysis={() => setShowMarketAnalysis(true)}
          onEnterShop={(merchantName) => {
            setCurrentMerchant(merchantName);
            setGameState('merchantShop');
          }}
          onPlayerDataUpdate={updatePlayerData}
          onPlaceBet={async (bet) => {
            if (economyAccount()) {
              try { await economyRequest('tournament/bets/confirm', { tournament_id: playerData.tournamentData.currentTournament.id, bets: [bet] }); return true; }
              catch (error) { alert(error.message); return false; }
            }
            const tournament = playerData?.tournamentData?.currentTournament;
            const currentBets = playerData?.tournamentData?.currentBets || [];
            const validation = validateBet(bet, tournament, currentBets, playerData?.gold || 0);
            if (!validation.valid) {
              alert(validation.error);
              return false;
            }
            const newBets = addBet(currentBets, bet);
            updatePlayerData({
              ...playerData,
              gold: playerData.gold - bet.amount,
              tournamentData: {
                ...playerData.tournamentData,
                currentBets: newBets,
              },
            });
            return true;
          }}
          onCancelBet={async (betId) => {
            if (economyAccount()) {
              try { await economyRequest('tournament/bets/cancel', { tournament_id: playerData.tournamentData.currentTournament.id, bet_id: betId }); return true; }
              catch (error) { alert(error.message); return false; }
            }
            const currentBets = playerData?.tournamentData?.currentBets || [];
            const bet = currentBets.find(b => b.id === betId);
            if (!bet) return false;
            const newBets = removeBet(currentBets, betId);
            updatePlayerData({
              ...playerData,
              gold: playerData.gold + bet.amount,
              tournamentData: {
                ...playerData.tournamentData,
                currentBets: newBets,
              },
            });
            return true;
          }}
          onPurchaseInfo={async (competitorId, infoType, tournament) => {
            if (economyAccount()) {
              try { await economyRequest('tournament/info/buy', { tournament_id: tournament.id, competitor_id: competitorId, info_type: infoType }); return true; }
              catch (error) { alert(error.message); return false; }
            }
            const validation = validateInfoPurchase({
              competitorId,
              infoType,
              tournament,
              purchasedInfo: playerData?.tournamentData?.purchasedInfo,
              playerGold: playerData?.gold || 0,
            });
            if (!validation.valid) {
              alert(validation.error);
              return false;
            }
            const price = getInfoPrice(tournament.type, infoType);
            const result = purchaseInfo(playerData.tournamentData, {
              competitorId,
              infoType,
              tournament,
            });
            if (result.success) {
              updatePlayerData({
                ...playerData,
                gold: playerData.gold - price,
                tournamentData: result.tournamentData,
              });
              return true;
            } else {
              alert(result.error);
              return false;
            }
          }}
          onClaimTournamentReward={handleClaimTournamentReward}
        />
        {/* 市場分析モーダル（商人ギルド画面用） */}
        {showMarketAnalysis && (
          <MarketAnalysis
            marketState={playerData?.market}
            allCards={allCards}
            assetHistory={playerData?.assetHistory}
            cardValueMap={cardValueMap}
            onClose={() => setShowMarketAnalysis(false)}
          />
        )}
      </>
    );
  }

  // 商人店内画面
  if (gameState === 'merchantShop' && currentMerchant) {
    return (
      <MerchantShop
        merchantName={currentMerchant}
        playerData={playerData}
        allCards={allCards}
        cardValueMap={cardValueMap}
        onBack={() => {
          setCurrentMerchant(null);
          setGameState('merchantGuild');
        }}
        onPlayerDataUpdate={updatePlayerData}
      />
    );
  }

  // 差し押さえ画面
  if (gameState === 'seizure') {
    const currentBattleNumber = playerData?.market?.currentDay || 1;
    const getCardValue = (card) => {
      const cardData = allCards?.find(c => c.id === card.cardId);
      if (!cardData) return 100;
      const cardValue = cardValueMap?.get?.(card.cardId);
      if (cardValue) {
        // レアリティに応じた価格を返す（半額が差し押さえ充当額になる）
        return cardValue.rarityValues?.[card.rarity] || cardValue.baseValue;
      }
      // fallback: レアリティを考慮して計算
      const baseValue = valueCalculator.calculateBaseValue(cardData);
      return valueCalculator.calculateRarityValue(baseValue, card.rarity);
    };

    return (
      <SeizureScreen
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        currentBattleNumber={currentBattleNumber}
        getCardValue={getCardValue}
        allCards={allCards}
        onComplete={(result) => {
          // 差し押さえ完了後の処理
          if (result.remainingDebt > 0) {
            // まだ借金が残っている場合は自動対戦ループへ
            console.log(`[Seizure] 残債あり: ${result.remainingDebt}G - 自動対戦ループへ`);
            setAutoBattleInitialDebt(result.remainingDebt);
            setGameState('autoBattleLoop');
            return;
          }
          // 対戦報酬があればゲームオーバー画面、なければタイトル画面へ
          if (battleReward) {
            setGameState('gameOver');
          } else {
            setGameState('title');
          }
        }}
      />
    );
  }

  // 自動対戦ループ画面
  if (gameState === 'autoBattleLoop') {
    return (
      <AutoBattleLoop
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        initialDebt={autoBattleInitialDebt}
        allCards={allCards}
        cardValueMap={cardValueMap}
        processDayAdvancement={processDayAdvancement}
        onComplete={(result) => {
          console.log(`[AutoBattleLoop] 完了: ${result.totalDays}日、余剰${result.cappedSurplus}G`);
          // 対戦報酬があればゲームオーバー画面、なければタイトル画面へ
          if (battleReward) {
            setGameState('gameOver');
          } else {
            setGameState('title');
          }
        }}
      />
    );
  }

  // パック開封画面
  if (gameState === 'packOpening' && pendingPackCards) {
    return (
      <PackOpening
        cards={pendingPackCards.cards}
        packCount={pendingPackCards.packCount}
        packInfo={pendingPackCards.packInfo}
        onClose={handlePackOpeningClose}
        existingCollection={playerData?.collection || []}
        effectLevel={playerData?.settings?.rarityEffectLevel}
      />
    );
  }

  // デッキ一覧画面
  if (gameState === 'deckList') {
    return (
      <DeckList
        playerData={playerData}
        allCards={allCards}
        onBack={() => setGameState('menu')}
        onCreateNew={() => {
          setEditingDeck(null);
          setGameState('deckEdit');
        }}
        onEditDeck={(deck) => {
          setEditingDeck(deck);
          setGameState('deckEdit');
        }}
        onDeleteDeck={handleDeleteDeck}
      />
    );
  }

  // デッキ編集画面
  if (gameState === 'deckEdit') {
    return (
      <DeckBuilder
        playerData={playerData}
        allCards={allCards}
        cardValueMap={cardValueMap}
        editingDeck={editingDeck}
        onBack={() => {
          setEditingDeck(null);
          setGameState('deckList');
        }}
        onSave={handleSaveDeck}
      />
    );
  }

  // ゲーム終了画面（Phase 1: 分離済み）
  if (gameState === 'gameOver') {
    return (
      <GameOverScreen
        winner={winner}
        battleReward={battleReward}
        setBattleReward={setBattleReward}
        playerData={playerData}
        allCards={allCards}
        cardValueMap={cardValueMap}
        showTournamentViewer={showTournamentViewer}
        pendingTournamentResult={pendingTournamentResult}
        setShowTournamentViewer={setShowTournamentViewer}
        setPendingTournamentResult={setPendingTournamentResult}
        awardBattleRewards={awardBattleRewards}
        updatePlayerData={updatePlayerData}
        handleOpenPack={handleOpenPack}
        handleClaimTournamentReward={handleClaimTournamentReward}
        onPlayAgain={adventureBattleContext ? () => { setAdventureBattleContext(null); setGameState('adventure'); } : initGame}
        onGoToMenu={() => { setAdventureBattleContext(null); setGameState('menu'); }}
      />
    );
  }

  // ゲーム画面
  return (
    <>
    <BattleProvider
      // Hook インスタンス
      selection={selection}
      setsuna={setsuna}
      gigen={gigen}
      miraiYochi={miraiYochi}
      sourei={sourei}
      hengenmi={hengenmi}
      deepSea={deepSea}
      // バトル状態
      currentPlayer={currentPlayer}
      phase={phase}
      turn={turn}
      isFirstTurn={isFirstTurn}
      winner={winner}
      p1Life={p1Life}
      p2Life={p2Life}
      p1Field={p1Field}
      p2Field={p2Field}
      p1Hand={p1Hand}
      p2Hand={p2Hand}
      p1Deck={p1Deck}
      p2Deck={p2Deck}
      p1Graveyard={p1Graveyard}
      p2Graveyard={p2Graveyard}
      p1ActiveSP={p1ActiveSP}
      p2ActiveSP={p2ActiveSP}
      p1RestedSP={p1RestedSP}
      p2RestedSP={p2RestedSP}
      p1FieldCard={p1FieldCard}
      p2FieldCard={p2FieldCard}
      p1PhaseCard={p1PhaseCard}
      p2PhaseCard={p2PhaseCard}
      p1StatusEffects={p1StatusEffects}
      p2StatusEffects={p2StatusEffects}
      // Setters
      setP1Life={setP1Life}
      setP2Life={setP2Life}
      setP1Field={setP1Field}
      setP2Field={setP2Field}
      setP1Hand={setP1Hand}
      setP2Hand={setP2Hand}
      setP1Deck={setP1Deck}
      setP2Deck={setP2Deck}
      setP1Graveyard={setP1Graveyard}
      setP2Graveyard={setP2Graveyard}
      setP1ActiveSP={setP1ActiveSP}
      setP2ActiveSP={setP2ActiveSP}
      setP1RestedSP={setP1RestedSP}
      setP2RestedSP={setP2RestedSP}
      setP1FieldCard={setP1FieldCard}
      setP2FieldCard={setP2FieldCard}
      setP1PhaseCard={setP1PhaseCard}
      setP2PhaseCard={setP2PhaseCard}
      // Actions
      summonCard={summonCard}
      executeAttack={executeAttack}
      executeSkill={executeSkill}
      processPhase={processPhase}
      nextPhase={nextPhase}
      chargeCard={chargeCard}
      chargeSP={chargeSP}
      attack={attack}
      addLog={addLog}
      // Other
      isMobile={isMobile}
      summoningSickness={summoningSickness}
      logs={logs}
    >
    <div style={mergeStyles(styles.container, mobileStyles.container, isMobile)}>
      {/* CSSアニメーション */}
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 10px currentColor; }
          50% { box-shadow: 0 0 25px currentColor, 0 0 40px currentColor; }
        }
      `}</style>

      {/* メインエリア（サイドパネル + ゲームボード） */}
      <div style={mergeStyles(styles.mainArea, mobileStyles.mainArea, isMobile)}>
        {/* サイドパネル（カード情報表示）- デスクトップのみ、位置可変 */}
        {!isMobile && cardInfoPosition !== 'hidden' && <div style={{
          ...styles.leftSidePanel,
          borderRight: cardInfoPosition === 'left' ? '1px solid rgba(107,76,230,0.3)' : 'none',
          borderLeft: cardInfoPosition === 'right' ? '1px solid rgba(107,76,230,0.3)' : 'none',
          order: cardInfoPosition === 'right' ? 1 : 0,
        }}>
          <div style={{ padding: '12px', borderBottom: '1px solid rgba(107,76,230,0.3)' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#a78bfa' }}>
              📋 カード情報
            </div>
          </div>
          <div style={styles.cardInfoPanel}>
            {/* ホバー中のカード（最優先表示） */}
            {hoveredCard && (
              <div>
                <div style={{ fontSize: '10px', color: '#a78bfa', marginBottom: '6px', padding: '4px', background: 'rgba(167,139,250,0.2)', borderRadius: '4px' }}>
                  👁️ プレビュー
                </div>
                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: hoveredCard.owner === 1 ? '#4da6ff' : '#ff8a8a' }}>
                  {hoveredCard.name}
                </div>
                <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '6px' }}>
                  属性: {hoveredCard.attribute} | コスト: {hoveredCard.cost} SP
                </div>
                {/* キーワード能力 */}
                {hoveredCard.keywordText && (
                  <div style={{ fontSize: '10px', color: '#ff8e53', marginBottom: '6px', padding: '4px', background: 'rgba(255,142,83,0.15)', borderRadius: '4px' }}>
                    {hoveredCard.keywordText}
                  </div>
                )}
                {/* 禁忌カード警告 */}
                {hoveredCard.isForbidden && (
                  <div style={{ fontSize: '10px', color: '#ff4444', marginBottom: '6px', padding: '4px', background: 'rgba(255,68,68,0.15)', borderRadius: '4px' }}>
                    ⚠️ 禁忌カード - デッキに1枚まで
                  </div>
                )}
                {hoveredCard.categoryText && (
                  <div style={{ fontSize: '11px', color: '#ffd700', marginBottom: '6px' }}>
                    カテゴリ: {hoveredCard.categoryText}
                  </div>
                )}
                {(hoveredCard.type === 'monster' || hoveredCard.currentHp !== undefined) && (() => {
                  // 常時効果による修正値を計算
                  const effectContext = {
                    currentPlayer,
                    effectOwner: hoveredCard.owner || currentPlayer,
                    p1Field,
                    p2Field,
                    p1Life,
                    p2Life,
                  };
                  const { modifier: atkMod, sources: atkSources } = hoveredCard.currentAttack !== undefined
                    ? continuousEffectEngine.getAttackModifierDetails(hoveredCard, effectContext)
                    : { modifier: 0, sources: [] };
                  const { modifier: hpMod, sources: hpSources } = hoveredCard.currentHp !== undefined
                    ? continuousEffectEngine.getHPModifierDetails(hoveredCard, effectContext)
                    : { modifier: 0, sources: [] };
                  const baseAtk = hoveredCard.currentAttack || hoveredCard.attack;
                  const effectiveAtk = baseAtk + atkMod;
                  const allSources = [...atkSources, ...hpSources].filter((s, i, arr) => arr.findIndex(x => x.name === s.name) === i);

                  return (
                    <>
                      <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '4px' }}>
                        <span style={{
                          color: atkMod > 0 ? '#4cff4c' : atkMod < 0 ? '#ff4c4c' : '#ccc',
                        }}>
                          ⚔️ {effectiveAtk}
                          {atkMod !== 0 && ` (${atkMod > 0 ? '+' : ''}${atkMod})`}
                        </span>
                        {' | '}
                        <span style={{
                          color: hpMod > 0 ? '#4cff4c' : hpMod < 0 ? '#ff4c4c' : '#ccc',
                        }}>
                          ❤️ {hoveredCard.currentHp !== undefined ? `${hoveredCard.currentHp}/${hoveredCard.maxHP || hoveredCard.hp}` : hoveredCard.hp}
                          {hpMod !== 0 && ` (${hpMod > 0 ? '+' : ''}${hpMod})`}
                        </span>
                      </div>
                      {/* 常時効果ソース表示 */}
                      {allSources.length > 0 && (
                        <div style={{ fontSize: '9px', color: '#888', marginBottom: '8px', paddingLeft: '8px' }}>
                          {atkSources.map((s, i) => (
                            <div key={`atk-${i}`} style={{ color: s.value > 0 ? '#4cff4c' : '#ff4c4c' }}>
                              └ {s.name}: ATK {s.value > 0 ? '+' : ''}{s.value}
                            </div>
                          ))}
                          {hpSources.map((s, i) => (
                            <div key={`hp-${i}`} style={{ color: s.value > 0 ? '#4cff4c' : '#ff4c4c' }}>
                              └ {s.name}: HP {s.value > 0 ? '+' : ''}{s.value}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* 状態異常表示 */}
                      {hoveredCard.statusEffects && hoveredCard.statusEffects.length > 0 && (
                        <div style={{ fontSize: '10px', marginBottom: '8px', padding: '4px', background: 'rgba(255,99,71,0.15)', borderRadius: '4px' }}>
                          <div style={{ color: '#ff6347', fontWeight: 'bold', marginBottom: '2px' }}>状態異常:</div>
                          {hoveredCard.statusEffects.map((effect, i) => {
                            const meta = STATUS_EFFECT_METADATA[effect.type] || {};
                            return (
                              <div key={i} style={{ color: meta.color || '#ccc', fontSize: '9px' }}>
                                {meta.icon || '?'} {meta.displayName || effect.type}
                                {effect.value > 0 && (effect.type === STATUS_EFFECT_TYPES.AWAKENED || effect.type === STATUS_EFFECT_TYPES.ATK_UP) && ` (+${effect.value})`}
                                {effect.value > 0 && effect.type === STATUS_EFFECT_TYPES.ATK_DOWN && ` (-${effect.value})`}
                                {effect.duration > 0 && ` (残${effect.duration}ターン)`}
                                {effect.removeChance && ` [${Math.round(effect.removeChance * 100)}%解除]`}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}
                <div style={{
                  fontSize: '11px',
                  color: '#e0e0e0',
                  background: 'rgba(0,0,0,0.3)',
                  padding: '8px',
                  borderRadius: '6px',
                  lineHeight: '1.5',
                  marginBottom: '8px',
                }}>
                  {getEffectWithoutSkills(hoveredCard.effect) || 'なし'}
                </div>
                {/* トリガー情報 */}
                {(() => {
                  const triggers = parseCardTriggers(hoveredCard);
                  if (triggers.length === 0) return null;
                  return (
                    <div style={{ fontSize: '10px', marginBottom: '8px', padding: '6px', background: 'rgba(157,76,230,0.15)', borderRadius: '4px' }}>
                      <div style={{ color: '#9d4ce6', fontWeight: 'bold', marginBottom: '6px' }}>📍 発動タイミング</div>
                      {triggers.map((trigger, i) => (
                        <div key={i}>
                          {i > 0 && <div style={{ borderTop: '1px solid rgba(157,76,230,0.3)', margin: '6px 0' }} />}
                          <div style={{ color: '#c9a0ff', fontWeight: 'bold', fontSize: '10px', marginBottom: '2px' }}>
                            【{trigger.displayDescription || getTriggerDisplayName(trigger.type)}】
                            {trigger.activationType === ACTIVATION_TYPES.OPTIONAL && <span style={{ color: '#ff8e53', fontWeight: 'normal', marginLeft: '4px' }}>(任意)</span>}
                          </div>
                          {trigger.description && (
                            <div style={{ color: '#ccc', fontSize: '9px', paddingLeft: '8px' }}>
                              {trigger.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {(hoveredCard.basicSkill || hoveredCard.advancedSkill) && (
                  <div style={{ fontSize: '10px', lineHeight: '1.4' }}>
                    {hoveredCard.basicSkill && (
                      <div style={{ padding: '6px', background: 'rgba(76,175,80,0.2)', borderRadius: '4px', marginBottom: '4px' }}>
                        <span style={{ color: '#4caf50', fontWeight: 'bold' }}>基本技:</span><br/>
                        {hoveredCard.basicSkill.text}
                      </div>
                    )}
                    {hoveredCard.advancedSkill && (
                      <div style={{ padding: '6px', background: 'rgba(255,152,0,0.2)', borderRadius: '4px' }}>
                        <span style={{ color: '#ff9800', fontWeight: 'bold' }}>上級技:</span><br/>
                        {hoveredCard.advancedSkill.text}
                      </div>
                    )}
                  </div>
                )}
                {/* フレーバーテキスト */}
                {hoveredCard.flavor && (
                  <div style={{ fontSize: '9px', color: '#666', marginTop: '8px', fontStyle: 'italic', borderTop: '1px solid rgba(107,76,230,0.2)', paddingTop: '6px' }}>
                    📖 {hoveredCard.flavor}
                  </div>
                )}
              </div>
            )}
            {/* 選択中の手札カード（通常モードまたは手札選択モード） */}
            {!hoveredCard && (selectedHandCard || pendingSelectedCard) && (
              (() => {
                const displayCard = pendingSelectedCard || selectedHandCard;
                const isPendingMode = !!pendingSelectedCard;
                return (
                  <div>
                    {isPendingMode && (
                      <div style={{ fontSize: '10px', color: '#ff8e53', marginBottom: '6px', padding: '4px', background: 'rgba(255,142,83,0.2)', borderRadius: '4px' }}>
                        🔍 選択確認中
                      </div>
                    )}
                    <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: currentPlayer === 1 ? '#4da6ff' : '#ff8a8a' }}>
                      {displayCard.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '6px' }}>
                      属性: {displayCard.attribute} | コスト: {displayCard.cost} SP
                    </div>
                    {/* キーワード能力 */}
                    {displayCard.keywordText && (
                      <div style={{ fontSize: '10px', color: '#ff8e53', marginBottom: '6px', padding: '4px', background: 'rgba(255,142,83,0.15)', borderRadius: '4px' }}>
                        {displayCard.keywordText}
                      </div>
                    )}
                    {/* 禁忌カード警告 */}
                    {displayCard.isForbidden && (
                      <div style={{ fontSize: '10px', color: '#ff4444', marginBottom: '6px', padding: '4px', background: 'rgba(255,68,68,0.15)', borderRadius: '4px' }}>
                        ⚠️ 禁忌カード - デッキに1枚まで
                      </div>
                    )}
                    {displayCard.categoryText && (
                      <div style={{ fontSize: '11px', color: '#ffd700', marginBottom: '6px' }}>
                        カテゴリ: {displayCard.categoryText}
                      </div>
                    )}
                    {displayCard.type === 'monster' && (
                      <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '8px' }}>
                        ⚔️ {displayCard.attack} | ❤️ {displayCard.hp}
                      </div>
                    )}
                    <div style={{
                      fontSize: '11px',
                      color: '#e0e0e0',
                      background: 'rgba(0,0,0,0.3)',
                      padding: '8px',
                      borderRadius: '6px',
                      lineHeight: '1.5',
                      marginBottom: '8px',
                    }}>
                      {getEffectWithoutSkills(displayCard.effect) || 'なし'}
                    </div>
                    {/* トリガー情報 */}
                    {(() => {
                      const triggers = parseCardTriggers(displayCard);
                      if (triggers.length === 0) return null;
                      return (
                        <div style={{ fontSize: '10px', marginBottom: '8px', padding: '6px', background: 'rgba(157,76,230,0.15)', borderRadius: '4px' }}>
                          <div style={{ color: '#9d4ce6', fontWeight: 'bold', marginBottom: '6px' }}>📍 発動タイミング</div>
                          {triggers.map((trigger, i) => (
                            <div key={i}>
                              {i > 0 && <div style={{ borderTop: '1px solid rgba(157,76,230,0.3)', margin: '6px 0' }} />}
                              <div style={{ color: '#c9a0ff', fontWeight: 'bold', fontSize: '10px', marginBottom: '2px' }}>
                                【{trigger.displayDescription || getTriggerDisplayName(trigger.type)}】
                                {trigger.activationType === ACTIVATION_TYPES.OPTIONAL && <span style={{ color: '#ff8e53', fontWeight: 'normal', marginLeft: '4px' }}>(任意)</span>}
                              </div>
                              {trigger.description && (
                                <div style={{ color: '#ccc', fontSize: '9px', paddingLeft: '8px' }}>
                                  {trigger.description}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    {displayCard.type === 'monster' && (displayCard.basicSkill || displayCard.advancedSkill) && (
                      <div style={{ fontSize: '10px', lineHeight: '1.4' }}>
                        {displayCard.basicSkill && (
                          <div style={{ padding: '6px', background: 'rgba(76,175,80,0.2)', borderRadius: '4px', marginBottom: '4px' }}>
                            <span style={{ color: '#4caf50', fontWeight: 'bold' }}>基本技:</span><br/>
                            {displayCard.basicSkill.text}
                          </div>
                        )}
                        {displayCard.advancedSkill && (
                          <div style={{ padding: '6px', background: 'rgba(255,152,0,0.2)', borderRadius: '4px' }}>
                            <span style={{ color: '#ff9800', fontWeight: 'bold' }}>上級技:</span><br/>
                            {displayCard.advancedSkill.text}
                          </div>
                        )}
                      </div>
                    )}
                    {/* フレーバーテキスト */}
                    {displayCard.flavor && (
                      <div style={{ fontSize: '9px', color: '#666', marginTop: '8px', fontStyle: 'italic', borderTop: '1px solid rgba(107,76,230,0.2)', paddingTop: '6px' }}>
                        📖 {displayCard.flavor}
                      </div>
                    )}
                    {/* 操作ガイドは通常モードのみ表示 */}
                    {!isPendingMode && (
                      <div style={{ fontSize: '10px', color: '#888', marginTop: '8px', padding: '6px', background: 'rgba(107,76,230,0.1)', borderRadius: '4px' }}>
                        {displayCard.type === 'monster' && '👆 空きスロットをクリックして召喚'}
                        {displayCard.type === 'magic' && '👆 「魔法カード発動」ボタンで発動'}
                        {displayCard.type === 'field' && '👆 フィールドゾーンをクリックして配置'}
                        {displayCard.type === 'phasecard' && '👆 フェイズゾーンをクリックして配置'}
                      </div>
                    )}
                  </div>
                );
              })()
            )}
            {/* 選択中のフィールドモンスター */}
            {!hoveredCard && !selectedHandCard && !pendingSelectedCard && selectedFieldMonster !== null && (() => {
              const field = currentPlayer === 1 ? p1Field : p2Field;
              const monster = field[selectedFieldMonster];
              if (!monster) return null;

              // 常時効果による修正値を計算
              const effectContext = {
                currentPlayer,
                effectOwner: currentPlayer,
                p1Field,
                p2Field,
                p1Life,
                p2Life,
              };
              const { modifier: atkMod, sources: atkSources } = continuousEffectEngine.getAttackModifierDetails(monster, effectContext);
              const { modifier: hpMod, sources: hpSources } = continuousEffectEngine.getHPModifierDetails(monster, effectContext);
              const baseAtk = monster.currentAttack || monster.attack;
              const effectiveAtk = baseAtk + atkMod;

              return (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: currentPlayer === 1 ? '#4da6ff' : '#ff8a8a' }}>
                    🎯 {monster.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '6px' }}>
                    属性: {monster.attribute}
                  </div>
                  {/* キーワード能力 */}
                  {monster.keywordText && (
                    <div style={{ fontSize: '10px', color: '#ff8e53', marginBottom: '6px', padding: '4px', background: 'rgba(255,142,83,0.15)', borderRadius: '4px' }}>
                      {monster.keywordText}
                    </div>
                  )}
                  {/* 禁忌カード警告 */}
                  {monster.isForbidden && (
                    <div style={{ fontSize: '10px', color: '#ff4444', marginBottom: '6px', padding: '4px', background: 'rgba(255,68,68,0.15)', borderRadius: '4px' }}>
                      ⚠️ 禁忌カード - デッキに1枚まで
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '4px' }}>
                    <span style={{
                      color: atkMod > 0 ? '#4cff4c' : atkMod < 0 ? '#ff4c4c' : '#ccc',
                    }}>
                      ⚔️ {effectiveAtk}
                      {atkMod !== 0 && ` (${atkMod > 0 ? '+' : ''}${atkMod})`}
                    </span>
                    {' | '}
                    <span style={{
                      color: hpMod > 0 ? '#4cff4c' : hpMod < 0 ? '#ff4c4c' : '#ccc',
                    }}>
                      ❤️ {monster.currentHP}/{monster.maxHP || monster.hp}
                      {hpMod !== 0 && ` (${hpMod > 0 ? '+' : ''}${hpMod})`}
                    </span>
                    {monster.charges && monster.charges.length > 0 && (
                      <span style={{ marginLeft: '8px', color: '#4caf50' }}>
                        ⚡ チャージ: {monster.charges.length}
                      </span>
                    )}
                  </div>
                  {/* 常時効果ソース表示 */}
                  {(atkSources.length > 0 || hpSources.length > 0) && (
                    <div style={{ fontSize: '9px', color: '#888', marginBottom: '8px', paddingLeft: '8px' }}>
                      {atkSources.map((s, i) => (
                        <div key={`atk-${i}`} style={{ color: s.value > 0 ? '#4cff4c' : '#ff4c4c' }}>
                          └ {s.name}: ATK {s.value > 0 ? '+' : ''}{s.value}
                        </div>
                      ))}
                      {hpSources.map((s, i) => (
                        <div key={`hp-${i}`} style={{ color: s.value > 0 ? '#4cff4c' : '#ff4c4c' }}>
                          └ {s.name}: HP {s.value > 0 ? '+' : ''}{s.value}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* 状態異常表示 */}
                  {monster.statusEffects && monster.statusEffects.length > 0 && (
                    <div style={{ fontSize: '10px', marginBottom: '8px', padding: '4px', background: 'rgba(255,99,71,0.15)', borderRadius: '4px' }}>
                      <div style={{ color: '#ff6347', fontWeight: 'bold', marginBottom: '2px' }}>状態異常:</div>
                      {monster.statusEffects.map((effect, i) => {
                        const meta = STATUS_EFFECT_METADATA[effect.type] || {};
                        return (
                          <div key={i} style={{ color: meta.color || '#ccc', fontSize: '9px' }}>
                            {meta.icon || '?'} {meta.displayName || effect.type}
                            {effect.value > 0 && (effect.type === STATUS_EFFECT_TYPES.AWAKENED || effect.type === STATUS_EFFECT_TYPES.ATK_UP) && ` (+${effect.value})`}
                            {effect.value > 0 && effect.type === STATUS_EFFECT_TYPES.ATK_DOWN && ` (-${effect.value})`}
                            {effect.duration > 0 && ` (残${effect.duration}ターン)`}
                            {effect.removeChance && ` [${Math.round(effect.removeChance * 100)}%解除]`}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {monster.categoryText && (
                    <div style={{ fontSize: '11px', color: '#ffd700', marginBottom: '6px' }}>
                      カテゴリ: {monster.categoryText}
                    </div>
                  )}
                  <div style={{
                    fontSize: '11px',
                    color: '#e0e0e0',
                    background: 'rgba(0,0,0,0.3)',
                    padding: '8px',
                    borderRadius: '6px',
                    lineHeight: '1.5',
                    marginBottom: '8px',
                  }}>
                    {getEffectWithoutSkills(monster.effect) || 'なし'}
                  </div>
                  {/* トリガー情報 */}
                  {(() => {
                    const triggers = parseCardTriggers(monster);
                    if (triggers.length === 0) return null;
                    return (
                      <div style={{ fontSize: '10px', marginBottom: '8px', padding: '6px', background: 'rgba(157,76,230,0.15)', borderRadius: '4px' }}>
                        <div style={{ color: '#9d4ce6', fontWeight: 'bold', marginBottom: '6px' }}>📍 発動タイミング</div>
                        {triggers.map((trigger, i) => (
                          <div key={i}>
                            {i > 0 && <div style={{ borderTop: '1px solid rgba(157,76,230,0.3)', margin: '6px 0' }} />}
                            <div style={{ color: '#c9a0ff', fontWeight: 'bold', fontSize: '10px', marginBottom: '2px' }}>
                              【{trigger.displayDescription || getTriggerDisplayName(trigger.type)}】
                              {trigger.activationType === ACTIVATION_TYPES.OPTIONAL && <span style={{ color: '#ff8e53', fontWeight: 'normal', marginLeft: '4px' }}>(任意)</span>}
                            </div>
                            {trigger.description && (
                              <div style={{ color: '#ccc', fontSize: '9px', paddingLeft: '8px' }}>
                                {trigger.description}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {(monster.basicSkill || monster.advancedSkill) && (
                    <div style={{ fontSize: '10px', lineHeight: '1.4' }}>
                      {monster.basicSkill && (
                        <div style={{ padding: '6px', background: 'rgba(76,175,80,0.2)', borderRadius: '4px', marginBottom: '4px' }}>
                          <span style={{ color: '#4caf50', fontWeight: 'bold' }}>基本技(1):</span><br/>
                          {monster.basicSkill.text}
                        </div>
                      )}
                      {monster.advancedSkill && (
                        <div style={{ padding: '6px', background: 'rgba(255,152,0,0.2)', borderRadius: '4px' }}>
                          <span style={{ color: '#ff9800', fontWeight: 'bold' }}>上級技(2):</span><br/>
                          {monster.advancedSkill.text}
                        </div>
                      )}
                    </div>
                  )}
                  {/* フレーバーテキスト */}
                  {monster.flavor && (
                    <div style={{ fontSize: '9px', color: '#666', marginTop: '8px', fontStyle: 'italic', borderTop: '1px solid rgba(107,76,230,0.2)', paddingTop: '6px' }}>
                      📖 {monster.flavor}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* 選択中のフィールド/フェイズカード */}
            {!hoveredCard && !selectedHandCard && selectedFieldMonster === null && selectedFieldCardInfo && (
              <div>
                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: selectedFieldCardInfo.player === 1 ? '#4da6ff' : '#ff8a8a' }}>
                  {selectedFieldCardInfo.type === 'field' ? '🌍 フィールド' : '⚡ フェイズ'}: {selectedFieldCardInfo.card.name}
                </div>
                <div style={{ fontSize: '11px', color: '#ccc', marginBottom: '6px' }}>
                  属性: {selectedFieldCardInfo.card.attribute} | コスト: {selectedFieldCardInfo.card.cost} SP
                </div>
                {/* キーワード能力 */}
                {selectedFieldCardInfo.card.keywordText && (
                  <div style={{ fontSize: '10px', color: '#ff8e53', marginBottom: '6px', padding: '4px', background: 'rgba(255,142,83,0.15)', borderRadius: '4px' }}>
                    {selectedFieldCardInfo.card.keywordText}
                  </div>
                )}
                {selectedFieldCardInfo.type === 'phasecard' && (
                  <>
                    <div style={{ fontSize: '12px', color: '#4caf50', marginBottom: '8px' }}>
                      ⚡ {getStageShortName(selectedFieldCardInfo.card.stage || 0)}段階 ({selectedFieldCardInfo.card.charges?.length || 0}/3)
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#ffd700',
                      background: 'rgba(255,215,0,0.1)',
                      padding: '8px',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      lineHeight: '1.5',
                    }}>
                      <strong>【現在の効果】</strong><br/>
                      {getCurrentStageDescription(selectedFieldCardInfo.card) || '効果なし'}
                    </div>
                    {(selectedFieldCardInfo.card.stage || 0) < 3 && getNextStageDescription(selectedFieldCardInfo.card) && (
                      <div style={{
                        fontSize: '11px',
                        color: '#4da6ff',
                        background: 'rgba(77,166,255,0.1)',
                        padding: '8px',
                        borderRadius: '6px',
                        lineHeight: '1.5',
                      }}>
                        <strong>【次の段階】</strong><br/>
                        {getNextStageDescription(selectedFieldCardInfo.card)}
                      </div>
                    )}
                  </>
                )}
                {selectedFieldCardInfo.type === 'field' && (
                  <div style={{
                    fontSize: '11px',
                    color: '#e0e0e0',
                    background: 'rgba(0,0,0,0.3)',
                    padding: '8px',
                    borderRadius: '6px',
                    lineHeight: '1.5',
                    marginBottom: '8px',
                  }}>
                    {getEffectWithoutSkills(selectedFieldCardInfo.card.effect) || 'なし'}
                  </div>
                )}
                {/* トリガー情報 */}
                {(() => {
                  const triggers = parseCardTriggers(selectedFieldCardInfo.card);
                  if (triggers.length === 0) return null;
                  return (
                    <div style={{ fontSize: '10px', marginBottom: '8px', padding: '6px', background: 'rgba(157,76,230,0.15)', borderRadius: '4px' }}>
                      <div style={{ color: '#9d4ce6', fontWeight: 'bold', marginBottom: '6px' }}>📍 発動タイミング</div>
                      {triggers.map((trigger, i) => (
                        <div key={i}>
                          {i > 0 && <div style={{ borderTop: '1px solid rgba(157,76,230,0.3)', margin: '6px 0' }} />}
                          <div style={{ color: '#c9a0ff', fontWeight: 'bold', fontSize: '10px', marginBottom: '2px' }}>
                            【{trigger.displayDescription || getTriggerDisplayName(trigger.type)}】
                            {trigger.activationType === ACTIVATION_TYPES.OPTIONAL && <span style={{ color: '#ff8e53', fontWeight: 'normal', marginLeft: '4px' }}>(任意)</span>}
                          </div>
                          {trigger.description && (
                            <div style={{ color: '#ccc', fontSize: '9px', paddingLeft: '8px' }}>
                              {trigger.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {/* フレーバーテキスト */}
                {selectedFieldCardInfo.card.flavor && (
                  <div style={{ fontSize: '9px', color: '#666', marginBottom: '8px', fontStyle: 'italic', borderTop: '1px solid rgba(107,76,230,0.2)', paddingTop: '6px' }}>
                    📖 {selectedFieldCardInfo.card.flavor}
                  </div>
                )}
                <button
                  onClick={() => setSelectedFieldCardInfo(null)}
                  style={{
                    marginTop: '8px',
                    padding: '4px 12px',
                    background: 'rgba(107,76,230,0.5)',
                    border: '1px solid #6b4ce6',
                    borderRadius: '4px',
                    color: '#fff',
                    fontSize: '11px',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  閉じる
                </button>
              </div>
            )}
            {/* 何も選択されていない時 */}
            {!selectedHandCard && selectedFieldMonster === null && !selectedFieldCardInfo && (
              <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', padding: '40px 10px' }}>
                カードを選択すると<br/>情報が表示されます
              </div>
            )}
          </div>
          {/* ログ */}
          <div style={{ padding: '12px', borderTop: '1px solid rgba(107,76,230,0.3)' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#888', marginBottom: '8px' }}>
              📜 ログ
            </div>
            <GameLog logs={logs} /> {/* Phase D-3: engineState直接参照 */}
          </div>
        </div>}

        {/* ゲームボード */}
        <div style={mergeStyles(styles.gameBoard, mobileStyles.gameBoard, isMobile)}>
        {/* 上部バー: ターン情報・フェイズ表示（新レイアウト） */}
        <TopBar
          onShowSidePanel={() => setShowSidePanel(true)}
          PHASES={PHASES}
          cardInfoPosition={cardInfoPosition}
          onCardInfoPositionChange={setCardInfoPosition}
          onHelpClick={() => setShowHelp(true)}
        />

        {/* プレイヤー2エリア（上）（Phase 2-4: 分離, Phase 5-3: Context化） */}
        <PlayerArea
          player={2}
          // Context に存在しない props のみ渡す
          bonusSP={p2BonusSP}
          playerType={p2PlayerType}
          opponentPlayerType={p1PlayerType}
          onHandCardClick={handleHandCardClick}
          onFieldSlotClick={handleFieldSlotClick}
          onFieldCardZoneClick={handleFieldCardZoneClick}
          onPhaseCardZoneClick={handlePhaseCardZoneClick}
          onGraveyardClick={() => setShowGraveyardViewer(2)}
          getModifiedCostInfo={getModifiedCostInfo}
          continuousEffectEngine={continuousEffectEngine}
          statusEffectEngine={statusEffectEngine}
          styles={styles}
          mobileStyles={mobileStyles}
          mergeStyles={mergeStyles}
          initialLifePoints={initialLifePoints}
        />

        {/* プレイヤー1エリア（下）（Phase 2-4: 分離, Phase 5-3: Context化） */}
        <PlayerArea
          player={1}
          // Context に存在しない props のみ渡す
          bonusSP={p1BonusSP}
          playerType={p1PlayerType}
          opponentPlayerType={p2PlayerType}
          onHandCardClick={handleHandCardClick}
          onFieldSlotClick={handleFieldSlotClick}
          onFieldCardZoneClick={handleFieldCardZoneClick}
          onPhaseCardZoneClick={handlePhaseCardZoneClick}
          onGraveyardClick={() => setShowGraveyardViewer(1)}
          getModifiedCostInfo={getModifiedCostInfo}
          continuousEffectEngine={continuousEffectEngine}
          statusEffectEngine={statusEffectEngine}
          styles={styles}
          mobileStyles={mobileStyles}
          mergeStyles={mergeStyles}
          initialLifePoints={initialLifePoints}
        />

        {/* 選択オーバーレイ（手札・モンスターターゲット） */}
        <SelectionOverlay />

        {/* 下部アクションバー（新レイアウト） */}
        <ActionFooter
          chargeUsedThisTurn={chargeUsedThisTurn}
          canActivateSourei={canActivateSourei}
          getSoureiEffect={getSoureiEffect}
          canActivateMiraiYochi={canActivateMiraiYochi}
          canActivateGigen={canActivateGigen}
          canActivateHengenmi={canActivateHengenmi}
          getCardMainPhaseTriggers={getCardMainPhaseTriggers}
          createTriggerContext={createTriggerContext}
          activateTrigger={activateTrigger}
          useMagicCard={useMagicCard}
          activatableGraveyardCards={activatableGraveyardCards}
          activateGraveyardCard={activateGraveyardCard}
          handleDirectAttack={handleDirectAttack}
          canIgnoreBlockWithIatsuko={canIgnoreBlockWithIatsuko}
          handleSubmerge={handleSubmerge}
        />
        </div>
      </div>

      {/* モバイル用カード詳細モーダル（Phase 2-3: 分離, Phase 5-5: Context化） */}
      <MobileCardDetailModal />

      {/* 墓地閲覧モーダル（Phase 2-2: 分離, Phase 5-5: Context化） */}
      <GraveyardViewerModal activateGraveyardCard={activateGraveyardCard} />

      {/* デッキトップ確認モーダル */}
      {pendingDeckReview && (
        <DeckReviewModal
          cards={pendingDeckReview.cards}
          title={pendingDeckReview.title}
          message={pendingDeckReview.message}
          allowReorder={pendingDeckReview.allowReorder}
          onConfirm={pendingDeckReview.onConfirm ? (reorderedCards) => {
            pendingDeckReview.onConfirm(reorderedCards);
            setPendingDeckReview(null);
          } : () => setPendingDeckReview(null)}
          onCancel={pendingDeckReview.onCancel ? () => {
            pendingDeckReview.onCancel();
            setPendingDeckReview(null);
          } : null}
          selectMode={
            // 新形式: selectMode を直接使用
            pendingDeckReview.selectMode ||
            // 旧形式: selectCount/count から変換
            (pendingDeckReview.selectCount || pendingDeckReview.count
              ? { enabled: true, count: pendingDeckReview.selectCount || pendingDeckReview.count || 1 }
              : undefined)
          }
          onSelect={
            // 新形式: onSelect を直接使用
            pendingDeckReview.onSelect ? (selectedCards, remainingCards) => {
              pendingDeckReview.onSelect(selectedCards, remainingCards);
              setPendingDeckReview(null);
            }
            // 旧形式: callback から変換
            : pendingDeckReview.callback ? (selectedCards, remainingCards) => {
              pendingDeckReview.callback(selectedCards, remainingCards);
              setPendingDeckReview(null);
            }
            : null
          }
        />
      )}

      {/* チェーン確認ダイアログ（Phase 2-1: 分離, Phase 5-5: Context化） */}
      <ChainConfirmationModal />

      {/* 刹那詠唱カード選択モーダル（新レイアウト） */}
      <SetsunaModal />

      {/* 深海領域モーダル */}
      {deepSea.showDeepSeaModal && (
        <DeepSeaModal
          isOpen={deepSea.showDeepSeaModal !== null}
          onClose={() => deepSea.setShowDeepSeaModal(null)}
          deepSeaZone={deepSea.getPlayerDeepSeaZone(deepSea.showDeepSeaModal)}
          player={deepSea.showDeepSeaModal}
          activeSP={deepSea.showDeepSeaModal === 1 ? p1ActiveSP : p2ActiveSP}
          emptyFieldSlots={(deepSea.showDeepSeaModal === 1 ? p1Field : p2Field).filter(s => s === null).length}
          costReduction={0}
          onSurface={(deepSeaIndex, fieldSlot) => {
            const player = deepSea.showDeepSeaModal!;
            const currentField = player === 1 ? p1Field : p2Field;
            const deepSeaZone = deepSea.getPlayerDeepSeaZone(player);

            // fieldSlot が -1 の場合、最初の空きスロットを探す
            let targetSlot = fieldSlot;
            if (targetSlot === -1) {
              targetSlot = currentField.findIndex(slot => slot === null);
              if (targetSlot === -1) {
                addLog('フィールドに空きスロットがありません', 'damage');
                return;
              }
            }

            // 浮上前に深度を取得（ON_SURFACEトリガー用）
            const surfaceDepth = deepSeaZone.cards[deepSeaIndex]?.depth || 1;
            const surfacingCard = deepSeaZone.cards[deepSeaIndex]?.card;

            // placeOnField関数: カードをフィールドに配置
            const placeOnField = (card: any, slot: number, bonus: any): boolean => {
              // 数値に確実に変換
              const baseAttack = Number(card.attack) || 0;
              const baseHp = Number(card.hp) || 0;
              const attackBonus = Number(bonus?.attackBonus) || 0;
              // 先制攻撃可能（深度3+）の場合は召喚酔いなし
              const canPreemptiveStrike = bonus?.canPreemptiveStrike || false;

              const monsterInstance = {
                ...card,
                currentHp: baseHp,
                maxHp: baseHp,
                attack: baseAttack + attackBonus,
                currentAttack: baseAttack + attackBonus, // UIでの表示に必要
                canAttack: canPreemptiveStrike, // 深度3+なら攻撃可能
                charges: [],
                uniqueId: card.uniqueId,
                owner: player, // 常時効果のターゲット判定用
              };

              if (player === 1) {
                setP1Field(prev => {
                  const newField = [...prev];
                  newField[slot] = monsterInstance;
                  return newField;
                });
              } else {
                setP2Field(prev => {
                  const newField = [...prev];
                  newField[slot] = monsterInstance;
                  return newField;
                });
              }

              // トリガー登録
              registerCardTriggers(card, player, slot);

              // 浮上後のフィールド状態を作成（React state更新は非同期のため、トリガーには更新後の状態を渡す）
              const updatedP1Field = player === 1
                ? [...p1Field.slice(0, slot), monsterInstance, ...p1Field.slice(slot + 1)]
                : p1Field;
              const updatedP2Field = player === 2
                ? [...p2Field.slice(0, slot), monsterInstance, ...p2Field.slice(slot + 1)]
                : p2Field;

              // ON_SURFACEトリガーを発火
              const surfaceTriggerContext = {
                currentPlayer: player,
                card: monsterInstance,
                slotIndex: slot,
                monsterIndex: slot,
                surfaceDepth: surfaceDepth,
                p1Life, p2Life,
                setP1Life, setP2Life,
                p1Field: updatedP1Field, // 浮上後のフィールド状態
                p2Field: updatedP2Field, // 浮上後のフィールド状態
                setP1Field, setP2Field,
                p1Hand, p2Hand,
                setP1Hand, setP2Hand,
                p1Deck, p2Deck,
                setP1Deck, setP2Deck,
                p1Graveyard, p2Graveyard,
                setP1Graveyard, setP2Graveyard,
                p1ActiveSP, p2ActiveSP,
                setP1ActiveSP, setP2ActiveSP,
                p1RestedSP, p2RestedSP,
                setP1RestedSP, setP2RestedSP,
                setPendingHandSelection,
                setPendingMonsterTarget,
                setPendingGraveyardSelection,
                setPendingDeckReview,
                addLog,
              };
              fireTrigger(TRIGGER_TYPES.ON_SURFACE, surfaceTriggerContext as any);

              return true;
            };

            // deductSP関数: SPを消費
            const deductSP = (amount: number) => {
              if (player === 1) {
                setP1ActiveSP(prev => prev - amount);
                setP1RestedSP(prev => prev + amount);
              } else {
                setP2ActiveSP(prev => prev - amount);
                setP2RestedSP(prev => prev + amount);
              }
            };

            deepSea.handleSurface(
              player,
              deepSeaIndex,
              targetSlot,
              0, // costReduction
              placeOnField,
              deductSP,
              addLog
            );
            deepSea.setShowDeepSeaModal(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* 檻解除選択ダイアログ */}
      {pendingCageRelease && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '450px',
              width: '90vw',
              border: '3px solid #8b4513',
              boxShadow: '0 0 40px rgba(139, 69, 19, 0.5)',
            }}
          >
            {/* ヘッダー */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h2 style={{
                margin: 0,
                color: '#8b4513',
                fontSize: '1.5rem',
                textShadow: '0 0 10px rgba(139, 69, 19, 0.5)',
              }}>
                🔒 檻の解除
              </h2>
            </div>

            {/* メッセージ */}
            <div style={{
              textAlign: 'center',
              marginBottom: '20px',
              color: '#fff',
              fontSize: '1.1rem',
              lineHeight: '1.6',
            }}>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ color: '#ffd700', fontWeight: 'bold' }}>
                  {pendingCageRelease.monsterName}
                </span>
                <span> は檻に閉じ込められています</span>
              </div>
              <div style={{ color: '#aaa', fontSize: '0.95rem' }}>
                SP{pendingCageRelease.spCost}を払って解除しますか？
              </div>
              <div style={{ color: '#888', fontSize: '0.85rem', marginTop: '8px' }}>
                (現在のSP: {currentPlayer === 1 ? p1ActiveSP : p2ActiveSP})
              </div>
            </div>

            {/* アクションボタン */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              marginTop: '20px',
            }}>
              <button
                onClick={() => {
                  const activeSP = currentPlayer === 1 ? p1ActiveSP : p2ActiveSP;
                  if (activeSP < pendingCageRelease.spCost) {
                    addLog(`SPが足りません（必要: ${pendingCageRelease.spCost}）`, 'damage');
                    setPendingCageRelease(null);
                    return;
                  }

                  // SP消費
                  const setActiveSP = currentPlayer === 1 ? setP1ActiveSP : setP2ActiveSP;
                  const setRestedSP = currentPlayer === 1 ? setP1RestedSP : setP2RestedSP;
                  setActiveSP(prev => prev - pendingCageRelease.spCost);
                  setRestedSP(prev => prev + pendingCageRelease.spCost);

                  // CAGED状態を解除
                  const setField = currentPlayer === 1 ? setP1Field : setP2Field;
                  setField(prev => prev.map((m, idx) => {
                    if (idx === pendingCageRelease.slotIndex && m) {
                      const newStatusEffects = (m.statusEffects || []).filter(
                        s => s.type !== STATUS_EFFECT_TYPES.CAGED
                      );
                      return { ...m, statusEffects: newStatusEffects };
                    }
                    return m;
                  }));

                  addLog(`SP${pendingCageRelease.spCost}を払って${pendingCageRelease.monsterName}を檻から解放した！`, 'heal');
                  setPendingCageRelease(null);

                  // 他にCAGEDモンスターがいるかチェック
                  const myField = currentPlayer === 1 ? p1Field : p2Field;
                  for (let i = 0; i < myField.length; i++) {
                    if (i === pendingCageRelease.slotIndex) continue; // 今解除したものはスキップ
                    const monster = myField[i];
                    if (monster && statusEffectEngine.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) {
                      setPendingCageRelease({
                        monsterName: monster.name,
                        slotIndex: i,
                        fieldPlayer: currentPlayer,
                        spCost: 1,
                      });
                      return;
                    }
                  }
                }}
                disabled={(currentPlayer === 1 ? p1ActiveSP : p2ActiveSP) < pendingCageRelease.spCost}
                style={{
                  background: (currentPlayer === 1 ? p1ActiveSP : p2ActiveSP) >= pendingCageRelease.spCost
                    ? 'linear-gradient(135deg, #4da6ff, #2980b9)'
                    : '#555',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  padding: '12px 24px',
                  cursor: (currentPlayer === 1 ? p1ActiveSP : p2ActiveSP) >= pendingCageRelease.spCost ? 'pointer' : 'not-allowed',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  boxShadow: (currentPlayer === 1 ? p1ActiveSP : p2ActiveSP) >= pendingCageRelease.spCost
                    ? '0 0 15px rgba(77, 166, 255, 0.4)'
                    : 'none',
                  opacity: (currentPlayer === 1 ? p1ActiveSP : p2ActiveSP) >= pendingCageRelease.spCost ? 1 : 0.5,
                }}
              >
                はい（SP{pendingCageRelease.spCost}消費）
              </button>
              <button
                onClick={() => {
                  addLog(`${pendingCageRelease.monsterName}は檻に閉じ込められたまま`, 'info');
                  setPendingCageRelease(null);
                }}
                style={{
                  background: 'linear-gradient(135deg, #555, #333)',
                  border: '1px solid #666',
                  borderRadius: '8px',
                  color: '#ccc',
                  padding: '12px 24px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                }}
              >
                いいえ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 大会観戦ダイアログ */}
      {showTournamentViewer && pendingTournamentResult && (
        <TournamentViewer
          tournament={pendingTournamentResult}
          currentBets={playerData?.tournamentData?.currentBets || []}
          onClaimReward={handleClaimTournamentReward}
          onClose={() => {
            setShowTournamentViewer(false);
            setPendingTournamentResult(null);
          }}
        />
      )}

      {/* 市場分析モーダル */}
      {showMarketAnalysis && (
        <MarketAnalysis
          marketState={playerData?.market}
          allCards={allCards}
          assetHistory={playerData?.assetHistory}
          cardValueMap={cardValueMap}
          onClose={() => setShowMarketAnalysis(false)}
        />
      )}

      {/* モバイル用サイドパネルモーダル */}
      {isMobile && showSidePanel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={() => setShowSidePanel(false)}
        >
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: '16px',
              background: 'rgba(15,15,30,0.98)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 閉じるボタン */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#a78bfa' }}>📋 カード情報</div>
              <button
                onClick={() => setShowSidePanel(false)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: '#fff',
                  fontSize: '18px',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            {/* ホバー中/選択中カード情報 */}
            {hoveredCard && (
              <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(167,139,250,0.1)', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: hoveredCard.owner === 1 ? '#4da6ff' : '#ff8a8a', marginBottom: '8px' }}>
                  {hoveredCard.name}
                </div>
                <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '4px' }}>
                  属性: {hoveredCard.attribute} | コスト: {hoveredCard.cost} SP
                </div>
                {hoveredCard.type === 'monster' && (
                  <div style={{ fontSize: '12px', color: '#ccc' }}>
                    ATK: {hoveredCard.currentAttack || hoveredCard.attack} / HP: {hoveredCard.currentHp || hoveredCard.hp}
                  </div>
                )}
                {hoveredCard.effect && (
                  <div style={{ fontSize: '11px', color: '#aaa', marginTop: '8px', lineHeight: 1.4 }}>
                    {hoveredCard.effect}
                  </div>
                )}
              </div>
            )}

            {/* ログ */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#888', marginBottom: '8px' }}>📜 ログ</div>
              <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                <GameLog logs={logs} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </BattleProvider>

    {/* ヘルプモーダル（ゲーム中） */}
    <HelpModal
      isOpen={showHelp}
      onClose={() => setShowHelp(false)}
    />
  </>
  );
}
